import React, { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import {
  Cadence,
  PickerOption,
  PickerPlan,
  PickerPremium,
  PickerSubscription,
  effectiveCadence,
  isPaidActiveSubscription,
  outranksRecommendedTier,
  paidActivePlanIds,
  plansByPriceAscending,
  pricedCadences,
  purchasablePlans,
  resolveAutoTopupPack,
} from '../lib/plans';
import { APPLE_SUBSCRIPTION_CODE } from '../lib/apple-subscription-refusal';
import { amountFor, formatMoney } from '../lib/money';
import { showsTaxInclusivePrices } from '../lib/picker-geo';
import { AppliedCoupon } from '../lib/coupon';
import { CREDITS_REFRESH_EVENT } from '../lib/use-credits';
import { useTranslation } from '../lib/i18n';
import { useNarrow } from '../lib/use-narrow';
import { useBillingCycleStep } from '../lib/billing-cycle-step-flag';
import { cardCadence, optionPurchasable, ownsOption } from '../lib/billing-cycle';
import { Button, ScrollArea, TextButton, Typography } from './symphony/ui';
// The repo's one Wix wordmark, drawn for the Connectors genome. Imported rather than redrawn: a
// second copy of a logotype is a second thing to get wrong when brand re-issues it.
import { WixWordmark } from './symphony/connectors/BrandGlyphs';
import { useProductName } from '../lib/use-brand';
import { premiumFetch } from '../lib/premium-fetch';
import { FeatureInfo, featureInfoKey } from './pricing/FeatureInfo';
import { MADEFOR_TEXT } from './pricing/picker-fonts';
import { CouponField } from './pricing/CouponField';
import { BillingCycleStep } from './pricing/BillingCycleStep';
import { usePurchaseAttribution } from '../lib/purchase-attribution-context';
import type { PurchaseAttribution } from '../lib/purchase-attribution';
import { EveryPlanStacked } from './pricing/EveryPlanStacked';
import { PricingFaq } from './pricing/PricingFaq';
import { Modal } from './Modal';
import { API_BASE } from '../lib/api-base';
import { createSwitchActionKeys } from '../lib/switch-action-keys';
import { discardAndRetry } from '../lib/pending-upgrade-escape';
import { applySwitchRejection, type SwitchRejectionAction } from '../lib/switch-rejection';
import { abandonHeldUpgrade, resolveHeldUpgrade, type BillingLane, type ScaStageReporter } from '../lib/upgrade-confirmation';
import { postScaStage } from '../lib/sca-stage-sink';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { useGenericBi } from '../lib/use-generic-bi';
import { useItemSeenBi } from '../lib/use-item-seen-bi';
import { countryLabel, readCountryPreview, withCountryPreview, writeCountryPreview } from '../lib/picker-country-override';
import { usePersonalSaleActive } from '../lib/use-personal-sale';
import { personalSaleAppliesToPlan, personalSaleDiscounted } from '../lib/personal-sale';

// AgentPaymentsService.SwitchPlan outcome (mirrors the proto enum) — kept as a local string
// union so this client component doesn't import server-side rpc types.
type SwitchOutcomeStr = 'UPGRADED_NOW' | 'DOWNGRADE_SCHEDULED' | 'UPGRADE_REQUIRES_ACTION';

// What one attempt at a plan switch settled as. Only `applied` and `scheduled` are the switch having
// happened; `unsettled` and `in_flight` mean money may still be moving, so neither is a failure either.
type SwitchAttempt = SwitchRejectionAction | 'applied' | 'scheduled' | 'canceled' | 'unsettled';

// A deferred plan change (downgrade) read from GET /api/premium/scheduled-change.
interface PendingChange {
  pendingPlanId: string | null;
  scheduledChangeDate: string | null; // ISO string over JSON
}

// A transient overlay toast. `leaving` drives the exit animation before it unmounts; `id` keys the
// enter animation so replacing the message re-animates.
type ToastTone = 'success' | 'error' | 'muted';
interface ToastState {
  id: number;
  tone: ToastTone;
  text: string;
  leaving: boolean;
}

// Symphony package picker — the in-app rendering of the MKT "Symphony PP"
// pricing design (Figma node 1117:22298, "Package Picker DESKTOP"). The three PAID
// cards (Max / Pro / Basic — the frame's fourth, Free, is no longer offered; the
// picker sells and the free tier is not for sale), a Monthly/Yearly billing toggle, a "Recommended"
// banner over Pro, and a credits dropdown on the Max card that folds Max+ (30k)
// into the same card.
//
// Plan details (names, prices, credits) come from GET /api/plans, which reads
// the AgentPlanService catalog. There is NO hardcoded fallback: an empty or
// unreachable catalog renders an explicit message, never invented prices.
//
// The visual language is the marketing page's: bone cards (#f3f3f3) with
// near-black ink, a lime "Get started" on the recommended tier, Archivo for
// headings/price, Wix Madefor for body, and a Tinos serif for "/ month". Fonts
// are loaded in pages/_document.tsx with graceful system fallbacks.

/**
 * "Symphony is part of WIX - Trusted by 250M+ people worldwide", under the picker headline.
 *
 * The line Base44's picker carries, with our product in place of theirs. It is one sentence with a
 * LOGOTYPE inside it, so the copy arrives as a template with a `{logo}` placeholder and is split
 * around it — the repo idiom (`BasedOnProvider` in personality/VoiceSection). Concatenating
 * "{productName} is part of" + the mark + the rest would force English word order onto the other 36
 * languages, and several of them do not put the parent company where English does.
 *
 * The product name is interpolated from `useProductName()` rather than written into the 37 bundles:
 * the rule this app already keeps for every other sentence naming itself.
 *
 * The wordmark is `currentColor`, so it takes the secondary ink of the line it sits in and flips with
 * the theme on its own. 34px wide is the wordmark's own ratio against a 14px line — sized so its
 * cap-height sits a touch above the text, which is how the frame reads it.
 */
function TrustLine() {
  const { t } = useTranslation();
  const productName = useProductName();
  const line = t('pricing.header.partOfWix', { productName, logo: '{logo}' });
  const [before, ...after] = line.split('{logo}');
  if (after.length === 0) return <Typography variant="p2" color="secondary">{line}</Typography>;
  return (
    <Typography
      variant="p2"
      color="secondary"
      component="p"
      style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}
    >
      {before}
      {/* The wordmark is decorative here — the sentence around it already says "part of Wix", so a
          screen reader announcing it again would read the company twice. */}
      <WixWordmark width={34} />
      {after.join('WIX')}
    </Typography>
  );
}

const ARCHIVO = '"Archivo", "Helvetica Neue", Arial, sans-serif';

// Debug cohort selector options (values mirror the CreditsMode enum).
const COHORTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'LEGACY_ONLY', label: 'Legacy' },
  { value: 'COMBINED', label: 'Combined' },
  { value: 'AGENT_PREMIUM_ONLY', label: 'New' },
];
const MADEFOR_DISPLAY = '"Wix Madefor Display", "Archivo", "Helvetica Neue", Arial, sans-serif';
const SERIF = '"Tinos", "Times New Roman", Times, serif';

// Ink re-pointed to the Symphony black ink token (DESIGN_SPEC textDefault #000000).
const INK = 'var(--sym-ink)';
const HEADLINE_INK = 'var(--sym-ink)';
const BODY_INK = 'var(--sym-ink)';
// The section and cards use the matching semantic surfaces, so the light Figma palette stays
// unchanged while dark mode gets a raised card instead of white under light ink.
const CARD_BG = 'var(--ds-surface-sunken)';
// The CTA surface. Figma draws #312F2F — a neutral near-black, not the purple-tinted
// --ds-purple-900 (#301838) this used to carry. `--ds-fill-standard-hover` carries that
// #333232 as a SEMANTIC (it is the standard fill's hover step), so it also flips to the
// near-white #DBDBDB in dark mode — where the raw `--ds-neutral-800` it used to name would
// have left a near-black CTA on the near-black page.
const CTA_FILL = 'var(--ds-fill-standard-hover)';
// The accent does not flip with the theme, so its ink stays dark in both modes.
const ACCENT_INK = 'var(--ds-neutral-800)';
// Brand accent — the CTA-hover fill + the "current plan" status. Driven by the
// cascading `--accent-rgb` token (set on <body data-brand> in globals.css), NOT hardcoded, so the
// picker matches whichever mount it renders on: green (#A4FF70) on the Symphony design mount
// (/symphony), lavender (#CCB4E6) on the Orion-design mounts (/orion-web, /symphony-web).
const ACCENT_RGB = 'var(--accent-rgb)';
const ACCENT_SOLID = `rgb(${ACCENT_RGB})`;
const accentA = (alpha: number) => `rgba(${ACCENT_RGB}, ${alpha})`;
const BADGE_BLUE = 'var(--ds-text-in-progress)'; // spec Blue (5)
// The recommended card's blue plate, measured off the frame and scaled to this pane's 370px track
// (the frame's card is 415, so ~0.89).
//
// How far the plate rises out of the card's top edge. The grid's row-gap has to CLEAR it, or the
// plate paints over the card stacked above it in the one-column layout — keep `row-gap` above this.
const RECOMMENDED_BANNER_RISE = 44;
// Air under the disclaimer at the fold. Landing it flush on the bottom edge is what the measurement
// gives you and it reads as clipped — and one sub-pixel of rounding away from actually clipping the
// last line. `border-box` on the wrapper is what keeps this INSIDE the measured screen rather than
// adding to it: this app has no global border-box reset, so min-height would otherwise size the
// content box and push the blue band down by exactly this much.
const FOLD_BREATHING_ROOM = 24;
// How far it bleeds past the card's other three edges. The frame's plate is 418 wide against a 415
// card — 1.5 either side — and the card carries its own 2px blue border inside that, so the frame
// reads as a ~3.5px band rather than the 2px a border alone gives.
const RECOMMENDED_PLATE_BLEED = 2;
// The PLATE's own radius, which is not the card's. The frame sets the plate at 7.5 and the card at
// 20: the outer silhouette is the tighter of the two, and the blue fills the difference at the
// corners. That is why the plate cannot be a box-shadow — a shadow inherits the card's radius, so
// it rounded the outer corners to 16 and read as too soft.
const RECOMMENDED_PLATE_RADIUS = 8;
// "Current plan" reuses the brand accent + ink, so the subscribed view stays in the design's
// palette (accent / blue / ink) rather than a foreign green.

// Find the {plan, option} across all cards for an AgentPlan id — to resolve the current
// subscription's plan name/price, and the pending-downgrade target's name.
/**
 * The nearest ancestor that scrolls vertically, or null when nothing does.
 *
 * Deliberately NOT `findScroller`'s walk: that one also requires `scrollHeight > clientHeight`, i.e.
 * that the element is already overflowing. Here the overflow is the thing being set up, so the test
 * has to be the declared `overflow-y` alone.
 */
function findScrollParent(from: HTMLElement | null): HTMLElement | null {
  let el: HTMLElement | null = from?.parentElement ?? null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

function findOption(plans: PickerPlan[] | null, agentPlanId?: string | null): { plan: PickerPlan; option: PickerOption } | null {
  if (!plans || !agentPlanId) return null;
  for (const plan of plans) {
    const option = plan.options.find((o) => o.agentPlanId === agentPlanId);
    if (option) return { plan, option };
  }
  return null;
}


function cadenceOf(sub?: PickerSubscription): Cadence {
  return sub?.period === 'YEARLY' ? 'yearly' : 'monthly';
}

function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// AgentPlan ids of a premium payload's real, paid (Stripe-backed) ACTIVE subscriptions — drives
// hasRealSubscription and the post-checkout/switch polls, which must react only to a paid subscription.
// (A FREE-tier sub has no providerSubscriptionId and is excluded; see paidActivePlanIds.)
function activeIds(p: PickerPremium | null): Set<string> {
  return paidActivePlanIds(p);
}

// Details for the switch confirmation dialog (a switch charges the card on file with no Stripe
// UI, so we confirm first). Built in the parent, which has the plans + premium to classify.
interface SwitchConfirm {
  agentPlanId: string;
  cadence: Cadence;
  /** Binds a persisted retry key to the paid subscription state this action started from. */
  actionSourceScope: string;
  targetPlanName: string;
  targetCreditsLabel: string;
  priceLabel: string; // e.g. "$40 / month"
  kind: 'upgrade' | 'downgrade' | 'unknown';
  currentPlanName: string;
  effectiveDate: string; // formatted current-period-end (downgrade takes effect then)
}

// The card on file (GET /api/premium/payment-method), display-only, shown on the upgrade review
// screen. hasCard=false → show generic copy. Never the full card number.
interface CardOnFile {
  hasCard: boolean;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export interface SymphonyPricingProps {
  /** Start Stripe Checkout for a plan option at a cadence. */
  onCheckout: (agentPlanId: string, cadence: Cadence, planKey: string, couponCode?: string) => void;
  /** Start Stripe Checkout for a one-time top-up pack (current-plan card only). */
  onTopup: (packId: string) => void;
  /** agentPlanId currently redirecting to Checkout, if any. */
  pendingId: string | null;
  /** Disable every CTA while a checkout is in flight. */
  disabled: boolean;
  /**
   * How the last checkout ended.
   *
   * `settling` is a purchase whose outcome we have not established yet — a redirect buyer came back
   * and Stripe has not answered. The money may well have moved, so it is neither a success nor a
   * failure: it says so, keeps buying disabled so the buyer cannot pay twice for it, and polls.
   *
   * `started` is a plan whose first period costs nothing — a free-time or 100%-off coupon. The buyer
   * stored a card and NO money moved, so it must not be announced as a payment; the subscription
   * behind it is still being created by the setup webhook, which is why it polls exactly like
   * `success` does.
   */
  status?: 'success' | 'cancelled' | 'started' | 'settling' | null;
  error?: string | null;
  /**
   * A just-completed purchase whose Stripe session is confirmed `paid`, but whose subscription the
   * async webhook may not have granted yet. Lets the picker flip to the subscribed view for the
   * bought plan immediately (the bought card highlights, the others become plan changes); the
   * /api/premium poll then supersedes it with the real subscription. The parent clears it once it's
   * no longer a fresh paid return.
   */
  optimisticPurchase?: { agentPlanId: string; cadence: Cadence } | null;
  /**
   * A deeplinked top-up request (?topUpPackageId=…, sent by the mobile apps): once plans + premium
   * resolve, auto-open checkout for this pack — but only when it's the pack the user's own
   * current-plan card offers (see resolveAutoTopupPack). No match → the plain picker, silently.
   */
  autoTopupPackId?: string | null;
}

// Ramped poll cadence for the post-checkout /api/premium wait: check fast early — the async webhook
// grant often lands within a few seconds — then widen to spare the endpoint on a slow grant. Same
// ~33s total budget as the old flat 3s×12, but the first three checks fall inside ~3s instead of one.
const POLL_DELAYS_MS = [1000, 1000, 1000, 1500, 2000, 2500, 3000, 3000, 3000, 3000, 3000, 3000, 3000, 3000];

export function SymphonyPricing({
  onCheckout,
  onTopup,
  pendingId,
  disabled,
  status,
  error,
  optimisticPurchase,
  autoTopupPackId,
}: SymphonyPricingProps) {
  const { t } = useTranslation();
  // Yearly is the default SELECTION — the toggle opens on it, and it sits first (see BillingToggle).
  // What actually renders is `cadence` below, derived from this plus what the market prices: a
  // monthly-only market never renders yearly, whatever is selected here.
  // What the READER chose, and `undefined` until they choose — not a default of yearly.
  //
  // The distinction carries the subscriber's own cadence. Yearly-by-default put a MONTHLY subscriber on
  // the yearly column, where `ownsOption` answers no, so their own card did not read as current and the
  // ⋯ holding "Cancel plan" was never drawn. Cancelling lives only on this page and both phones open
  // this page, so that was a paying customer with no cancel path on any surface; two users reported it,
  // one calling the product a scam. `cycleUnasked` fixed the variant arm, which has no toggle; the
  // control arm has one, so the answer there is to OPEN it on the right side.
  const [selectedCadence, setSelectedCadence] = useState<Cadence | undefined>(undefined);
  const [plans, setPlans] = useState<PickerPlan[] | null>(null);
  // ISO currency for the whole picker (US→usd, GB→gbp), from GET /api/plans; drives the price symbol.
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  // The resolved pricing geo from the same response. Read for ONE thing: whether the prices shown
  // include tax, which decides the disclaimer (see showsTaxInclusivePrices). Deliberately not used to
  // re-derive anything about price or eligibility — the server already resolved both.
  const [geo, setGeo] = useState<string | undefined>(undefined);
  const [billingLane, setBillingLane] = useState<'live' | 'symphony-sandbox'>('live');
  const [loadError, setLoadError] = useState<string | null>(null);
  // Current-user premium (credits + subscriptions). Loaded independently of plans and never
  // blocks rendering — if it fails (e.g. the get_agent_premium grant isn't live yet) the picker
  // behaves exactly as before. `premiumError` + the raw payloads feed the debug panel.
  const [premium, setPremium] = useState<PickerPremium | null>(null);
  const [premiumError, setPremiumError] = useState<string | null>(null);
  // Whether the FIRST premium fetch has resolved (success or error). We gate the cards on this
  // so we never flash the not-subscribed layout (every card offering a first purchase) before premium
  // loads and then snap to the subscribed layout. Errors also flip it → graceful not-subscribed.
  const [premiumLoaded, setPremiumLoaded] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  // Whether the caller may use the debug chrome — the BO premiums write allowlist, said by the server
  // on the /api/plans response, NOT "is a Wix employee". Gates only the dot's VISIBILITY (every action
  // behind it is separately server-gated), and it must be the SAME answer picker-debug gives or the dot
  // opens a panel that 403s. Defaults false: no dot until the server says so.
  const [canPreview, setCanPreview] = useState(false);
  // Debug country preview (allowlisted callers only, server-enforced): re-prices the whole picker as a buyer in
  // that country. Read straight from storage so the FIRST /api/plans call already carries it — a
  // preview applied one render late would flash the caller's own prices on every load.
  const [countryPreview, setCountryPreview] = useState(readCountryPreview);
  // Gate the debug affordance's portal on a client mount — createPortal(…, document.body) can't run
  // during SSR (no document), and the portal is what lifts the dot above the app-wide FloatingChat.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Plan-switching state (existing subscribers only). A switch reuses the card on file — no
  // Stripe checkout — so it's handled here, not via the parent's onCheckout.
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null); // a deferred downgrade
  const [switchingId, setSwitchingId] = useState<string | null>(null); // agentPlanId being switched to
  // Transient feedback is a single overlay toast — never an inline banner (which shifted the whole
  // picker) and never a stack. A new toast replaces the current one in place; identical back-to-back
  // messages are de-duped so a re-fired action doesn't re-animate. Auto-dismisses; error stays a beat
  // longer. See <Toast> + the showToast/dismissToast helpers below.
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastSeq = useRef(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast((cur) => (cur ? { ...cur, leaving: true } : null));
  }, []);
  const showToast = useCallback((tone: ToastTone, text: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast((cur) => {
      // De-dupe: an identical message already showing is left in place (no re-animation, no flicker).
      if (cur && !cur.leaving && cur.tone === tone && cur.text === text) return cur;
      toastSeq.current += 1;
      return { id: toastSeq.current, tone, text, leaving: false };
    });
  }, []);
  const [confirm, setConfirm] = useState<SwitchConfirm | null>(null);
  // A switch the service refused because an EARLIER held upgrade is still awaiting payment. Keeps the
  // whole action so the discard-and-retry modal can replay it verbatim, plus the lane the rejection
  // resolved — voiding the hold is a write against one specific Stripe account.
  const [pendingUpgrade, setPendingUpgrade] =
    useState<{ confirm: SwitchConfirm; billingLane: BillingLane } | null>(null);
  // The state drives the button ("Discarding…", disabled); the ref is what the discard's re-entrancy
  // guard reads. A state read is the render's snapshot, so two presses landing before React commits
  // both see `false` and both void the hold and replay the switch — the exact race the escape's own
  // contract promises cannot happen. A ref is written before the first await, so the second press sees it.
  const [discardingHold, setDiscardingHold] = useState(false);
  const discardingHoldRef = useRef(false);
  const [cardOnFile, setCardOnFile] = useState<CardOnFile | null>(null);
  const [undoing, setUndoing] = useState(false);

  // Stripe idempotency tokens for plan switches — one per user ACTION, kept across a retry of that
  // action and released once it definitively ends. See lib/switch-action-keys for why that lifetime
  // (not per request, not per confirm-dialog) is what makes a double-submit charge once.
  const switchActionKeys = useRef(createSwitchActionKeys());

  // Auto-dismiss: once a fresh toast is shown, start its exit after a beat (errors linger longer so
  // they're readable). Re-runs whenever the shown toast changes; the leaving pass is a no-op here.
  useEffect(() => {
    if (!toast || toast.leaving) return;
    toastTimer.current = setTimeout(dismissToast, toast.tone === 'error' ? 6500 : 4000);
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [toast, dismissToast]);

  // A checkout error from the parent (embedded-checkout / start-checkout failures) → one error toast.
  useEffect(() => {
    if (error) showToast('error', error);
  }, [error, showToast]);

  // Returning from a completed Stripe checkout (parent flips `status` once, then strips the URL param
  // so a refresh can't replay it) → a single confirmation toast.
  useEffect(() => {
    if (status === 'success') showToast('success', t('pricing.banner.paymentReceived'));
    // Deliberately NOT "Payment received": nothing was charged.
    else if (status === 'started') showToast('success', t('pricing.banner.planStarting'));
    // Deliberately muted, not a success: we do not yet know that it worked.
    else if (status === 'settling') showToast('muted', t('pricing.banner.confirmingPayment'));
    else if (status === 'cancelled') showToast('muted', t('pricing.banner.checkoutCancelled'));
  }, [status, showToast, t]);
  // A pending cancel-at-period-end on the active subscription (from scheduled-change's
  // `pendingCancellation`). ISO date the plan lapses, or null when nothing is scheduled.
  const [cancellationDate, setCancellationDate] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Clear before fetching (a no-op on the mount run, where everything is still empty) so a
        // preview change shows the loading state, never the PREVIOUS country's prices/currency —
        // stale cross-country state is how a CA preview once rendered a leftover EUR.
        setLoadError(null);
        setPlans(null);
        setCurrency(undefined);
        const res = await fetch(withCountryPreview(`${API_BASE}/api/plans`, countryPreview), { credentials: 'include' });
        const json = (await res.json()) as {
          plans?: PickerPlan[];
          currency?: string;
          geo?: string;
          canPreviewCountry?: boolean;
          billingLane?: 'live' | 'symphony-sandbox';
          message?: string;
          error?: string;
        };
        if (cancelled) return;
        if (!res.ok) {
          setLoadError(json.message || json.error || t('pricing.error.loadPlans'));
          return;
        }
        // Only cards that can be bought — see `purchasablePlans` for the rolling-deploy window
        // where an un-updated pod can still hand this browser the option-less Free card.
        setPlans(purchasablePlans(json.plans ?? []));
        setCurrency(json.currency);
        setGeo(json.geo);
        setCanPreview(json.canPreviewCountry === true);
        setBillingLane(json.billingLane === 'symphony-sandbox' ? 'symphony-sandbox' : 'live');
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : t('pricing.error.loadPlans'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-loads when the debug country preview changes (a no-op for everyone else, whose preview is
    // always ""). `t` is only a static error-fallback and mustn't re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryPreview]);

  // Fetch the caller's premium once; returns it so a poller can inspect it. Never throws.
  //
  // Through `premiumFetch`, not a bare `fetch`: `/api/premium/*` is deliberately outside `apiFetch`
  // (it is not instance-auth aware), so that module is the only place the dev mock layer can see
  // these routes — and this call is the one the panel's "Plan tier" row exists to move. Read bare,
  // the row moved the sidebar's credits card and left the picker showing "Get started" on the plan
  // the account was supposedly on, which is exactly the silent-no-op `lib/premium-fetch.ts` warns
  // about. Off a dev build it performs the identical request.
  const loadPremium = useCallback(async (signal?: { cancelled: boolean }): Promise<PickerPremium | null> => {
    try {
      const res = await premiumFetch(`${API_BASE}/api/premium`);
      const json = (await res.json()) as { premium?: PickerPremium | null; error?: string };
      if (signal?.cancelled) return null;
      setPremium(json.premium ?? null);
      setPremiumError(json.error ?? null);
      setPremiumLoaded(true);
      return json.premium ?? null;
    } catch (err) {
      if (!signal?.cancelled) {
        setPremiumError(err instanceof Error ? err.message : 'Could not load premium.');
        setPremiumLoaded(true);
      }
      return null;
    }
  }, []);

  // Initial load.
  useEffect(() => {
    const signal = { cancelled: false };
    loadPremium(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadPremium]);

  // After a successful checkout the subscription is granted by an ASYNC Stripe webhook, so it's
  // usually not recorded the instant we land back on the picker. Poll /api/premium until the new
  // subscription appears, so the current-plan view shows up on its own without a manual refresh.
  const pollingRef = useRef(false);
  // Poll /api/premium until `done` is satisfied (or we give up after the ~33s budget). Used both on return
  // from checkout (wait for the first subscription to be granted by the async webhook) and after
  // an in-place upgrade (wait for the switched-to plan to become the active one).
  const pollPremium = useCallback(
    (done: (p: PickerPremium | null) => boolean) => {
      if (pollingRef.current) return; // already polling
      pollingRef.current = true;
      let attempts = 0; // ticks completed so far; indexes POLL_DELAYS_MS for the NEXT gap
      const tick = async () => {
        const p = await loadPremium();
        if (done(p) || attempts >= POLL_DELAYS_MS.length) {
          pollingRef.current = false; // done (found it, or gave up after the full ~33s budget)
          return;
        }
        setTimeout(tick, POLL_DELAYS_MS[attempts]);
        attempts += 1;
      };
      void tick();
    },
    [loadPremium],
  );

  // Trigger the poll on return from checkout. Two independent triggers so it fires regardless of
  // how the return is surfaced: (1) the raw URL carries ?status=success after Stripe's redirect —
  // read it directly on mount, not via the parent's verify→paymentOutcome→prop chain, which can
  // lag or, if the session-status probe flakes, never flip; (2) the `status` prop as a backup.
  useEffect(() => {
    if (typeof window !== 'undefined' && /[?&]status=success(?:&|$)/.test(window.location.search)) {
      pollPremium((p) => activeIds(p).size > 0);
    }
  }, [pollPremium]);
  useEffect(() => {
    // `started` polls too: its subscription is created asynchronously by the setup webhook, so the
    // picker has to wait for it exactly as it waits for a webhook-driven grant.
    // `settling` polls hardest of the three: the poll is what actually resolves it, since Stripe
    // never gave us a definitive answer.
    if (status === 'success' || status === 'started' || status === 'settling') {
      pollPremium((p) => activeIds(p).size > 0);
    }
  }, [status, pollPremium]);

  // The paid plan(s) that drive the "subscribed" view: real paid subs + the optimistic just-purchased
  // plan (see paidActivePlanIds). A FREE-tier sub is excluded so it never flips the picker to
  // "subscribed", which would route paid cards through SwitchPlan — and there is no Stripe
  // subscription behind a free tier for SwitchPlan to modify.
  const activePlanIds = useMemo(
    () => paidActivePlanIds(premium, optimisticPurchase?.agentPlanId),
    [premium, optimisticPurchase],
  );
  // The cadence (monthly/yearly) the user owns each paid plan at. A plan is "current" only at the
  // cadence they're actually subscribed to — so the SAME plan's other cadence reads as an
  // upgrade/downgrade offer, not "Current plan". (One active sub per namespace, but a Map is robust.)
  const subCadenceByPlanId = useMemo(() => {
    const m = new Map<string, Cadence>();
    for (const s of premium?.subscriptions ?? []) {
      if (isPaidActiveSubscription(s)) m.set(s.planId, cadenceOf(s));
    }
    if (optimisticPurchase) m.set(optimisticPurchase.agentPlanId, optimisticPurchase.cadence);
    return m;
  }, [premium, optimisticPurchase]);
  // A held payment may finish while this tab is reloading. Once its target is observable as the
  // active paid plan, the persisted retry key has reached a definitive end and must not be reused by
  // a future switch back to that plan.
  useEffect(() => {
    for (const [planId, activeCadence] of subCadenceByPlanId) {
      switchActionKeys.current.releaseTarget(planId, activeCadence);
    }
  }, [subCadenceByPlanId]);
  const isSubscribed = activePlanIds.size > 0;
  // Whether the user has a REAL (webhook-granted) active subscription — distinct from `isSubscribed`,
  // which also counts the optimistic post-purchase seed. Plan SWITCHING (SwitchPlan against the card
  // on file) needs a real subscription server-side, so it's gated on this, NOT the optimistic view —
  // otherwise a switch clicked during the optimistic window would hit /switch-plan with no sub yet.
  const hasRealSubscription = useMemo(() => activeIds(premium).size > 0, [premium]);
  const paidSub = useMemo(() => (premium?.subscriptions ?? []).find(isPaidActiveSubscription), [premium]);

  // Deeplinked top-up: fire once, and only after BOTH loads settle — matching against half-loaded
  // data would misread a valid request as "no match". One shot either way: a non-matching request
  // degrades to the plain picker (never an error), and a match must not re-open the checkout the
  // user just closed.
  const autoTopupFired = useRef(false);
  useEffect(() => {
    if (autoTopupFired.current || !autoTopupPackId || !plans || !premiumLoaded) return;
    autoTopupFired.current = true;
    const pack = resolveAutoTopupPack(plans, activeIds(premium), autoTopupPackId);
    if (pack) onTopup(pack.packId);
  }, [autoTopupPackId, plans, premiumLoaded, premium, onTopup]);

  // Every card the catalog returns is rendered. There is no per-user filtering left: the picker
  // sells, so a card exists only if it can be bought, and that is the same question for everyone.
  // (This used to hold a filtered view, because the Free card had to disappear once the buyer paid.)

  // Whether the buyer already outranks the recommended tier (a Max/Max+ subscriber being shown
  // "Recommended" on Pro).
  const outranksRecommended = useMemo(() => outranksRecommendedTier(plans, activePlanIds), [plans, activePlanIds]);

  // Personal sale: while the offer is live the picker frames BOTH the recommended (Pro) and the top
  // tier (Max) in blue and marks them "30% OFF" instead of "Recommended", and shows the discounted
  // price with the original struck through — but ONLY on yearly, the cadence the offer discounts.
  // Display-only (no coupon is applied at checkout yet); see lib/personal-sale.
  const saleActive = usePersonalSaleActive();
  // The most expensive plan's key (Max) — the tier the sale frames alongside the recommended one.
  const topTierPlanKey = useMemo(() => {
    if (!plans?.length) return null;
    const ascending = plansByPriceAscending(plans);
    return ascending[ascending.length - 1]?.key ?? null;
  }, [plans]);

  // Which cadences this market prices (see `pricedCadences`) — `null` until the catalog loads.
  const cadencesPriced = useMemo(() => pricedCadences(plans), [plans]);

  // Card ORDER is the only thing the viewport changes here: desktop keeps the server's Figma order
  // (Max, Pro, Basic); the mobile layout reads cheapest-first (Basic → Pro → Max).
  const narrow = useNarrow();
  const displayPlans = useMemo(
    () => (plans && narrow ? plansByPriceAscending(plans) : plans),
    [plans, narrow],
  );

  // The cadence everything below RENDERS at. Derived here, synchronously, and NOT corrected in an
  // effect: an effect runs after commit and after paint, so the render that first commits a
  // monthly-only catalog would paint "not available yearly" on every card — with the toggle already
  // hidden — before flipping. Reachable whenever /api/premium resolves before /api/plans. See
  // `effectiveCadence`.
  // Derived DURING RENDER, never corrected by an effect — an effect runs after paint, so the first
  // frame would show the wrong column and then flip. That is the same reason `effectiveCadence` is a
  // pure call here, and the regression `priced-cadences.test.ts` guards.
  //
  // The reader's own pick wins; failing that, the cadence they already pay at (only when there is
  // exactly one, or this would be choosing which of their subscriptions to centre on); failing that,
  // yearly, which is the side the toggle has always opened on.
  const ownedCadences = useMemo(() => [...new Set(subCadenceByPlanId.values())], [subCadenceByPlanId]);
  const baseCadence: Cadence = selectedCadence ?? (ownedCadences.length === 1 ? ownedCadences[0] : 'yearly');
  const cadence = effectiveCadence(baseCadence, cadencesPriced);

  // The A/B arm that asks the billing cycle on its OWN screen (`symphonyBillingCycleStepWeb`).
  // `undefined` until the gate answers — see `useBillingCycleStep` for why that state exists rather
  // than defaulting to control.
  const cycleStepArm = useBillingCycleStep();
  // The variant is only meaningful where there are two cycles to choose between. A monthly-only
  // market (India — see `pricedCadences`) would otherwise get a whole extra screen presenting one
  // option, so it stays on control there and the cards simply show monthly prices, as they do today.
  // Gated on the catalog having loaded, so the step is never entered against prices we don't have.
  const cycleStepOn = cycleStepArm === true && cadencesPriced?.yearly === true && cadencesPriced?.monthly === true;
  // The plan option whose cycle the buyer is choosing, or `null` on the cards.
  const [cycleTarget, setCycleTarget] = useState<{ plan: PickerPlan; option: PickerOption } | null>(null);
  const paneRef = useRef<HTMLDivElement | null>(null);
  const foldRef = useRef<HTMLDivElement | null>(null);
  const cardsScrollTopRef = useRef(0);
  /** The pane's own scroll container, or the document when the picker is not inside one. */
  const scrollerRef = useRef<Element | null>(null);
  const findScroller = useCallback((): Element => {
    let el: HTMLElement | null = paneRef.current?.parentElement ?? null;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) break;
      el = el.parentElement;
    }
    return el ?? document.scrollingElement ?? document.documentElement;
  }, []);
  /**
   * Make the first screen exactly one screen tall, so it ends on the disclaimer.
   *
   * Measured, not expressed in `dvh`, for two reasons the CSS cannot see: the scroll container is the
   * PANE rather than the document, and this wrapper does not start at its top edge (the pane carries
   * 72px of padding, and on narrow there is an app banner above it). So the number wanted is the
   * scroller's own client height minus wherever the wrapper begins inside it.
   *
   * Written to `min-height`, so it is a floor and never a cap: taller content overflows it normally
   * and the disclaimer falls below the fold, which is the only honest answer on a short window.
   *
   * The `ResizeObserver` watches the SCROLLER, not the wrapper. The scroller's box changes when the
   * window resizes or the app banner appears — the things that move the fold — while the wrapper's
   * own height is what this effect writes, so observing it instead would be a feedback loop.
   */
  useEffect(() => {
    const scroller = findScrollParent(foldRef.current);
    if (!scroller) return;
    const measure = () => {
      const el = foldRef.current;
      if (!el) return;
      // Cleared first: the wrapper's own floor is part of the layout the next reading is taken from,
      // and on a shrinking window a stale floor keeps the scroller scrolled and skews the offset.
      el.style.minHeight = '';
      const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const height = scroller.clientHeight - top;
      if (height > 0) el.style.minHeight = `${Math.round(height)}px`;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
    // Re-measured when the screen swaps or the cards first appear: both change where the wrapper
    // starts and how tall its content is.
  }, [cycleTarget, plans, premiumLoaded, cycleStepArm]);

  /**
   * Carry the scroll position across the two screens.
   *
   * The step REPLACES the cards inside the page's scroll container rather than opening over them, so
   * nothing resets that container's `scrollTop` on the swap — the browser merely CLAMPS it to the
   * shorter content. On a phone the buyer has usually scrolled down to reach the card they tapped, so
   * they land part-way down the step with its heading above the fold: measured at 723px past it on a
   * 320-wide screen, 269px on a 375. They see two prices and a Continue button and no question.
   *
   * Restoring the card position on the way back is the other half — a Back that dumps you at the top
   * of a list you had scrolled through reads as having lost your place.
   *
   * The remembered offset is captured in the CLICK (`rememberCardsScroll`), not here: by the time an
   * effect runs React has already swapped in the shorter step and the browser has already clamped
   * `scrollTop`, so reading it here stores the clamped number and Back restores the wrong place.
   */
  const rememberCardsScroll = useCallback(() => {
    const scroller = findScroller();
    scrollerRef.current = scroller;
    cardsScrollTopRef.current = scroller.scrollTop;
  }, [findScroller]);
  useEffect(() => {
    const scroller = scrollerRef.current ?? findScroller();
    if (cycleTarget) {
      scroller.scrollTop = 0;
    } else if (cardsScrollTopRef.current > 0) {
      // The cards re-render in the same commit this effect runs after, so the container is already
      // tall enough to take the old offset back — no clamp, no rAF.
      scroller.scrollTop = cardsScrollTopRef.current;
      cardsScrollTopRef.current = 0;
    }
  }, [cycleTarget, findScroller]);
  // Cleared whenever the arm resolves to control, so a dev flipping the dev-tools toggle mid-session
  // can't be stranded on a screen the control arm has no way back to.
  useEffect(() => {
    if (!cycleStepOn) setCycleTarget(null);
  }, [cycleStepOn]);
  /**
   * The arm, on a CLIENT event, so the experiment can actually be read.
   *
   * The step reports its own impression, which gives the variant a denominator — but the control had
   * no matching marker, so outside an app-log line there was no way to split the picker's population
   * into arms at all. That gap is not academic: the gate is `petriOpen || configOpen`, and only the
   * Petri leg registers an exposure. Ramp both legs to 50% and ~75% of buyers get the variant while
   * Petri believes 50% do — a quarter of its "control" is being shown the variant, and the
   * platform's own comparison is then measuring a contaminated baseline.
   *
   * Emitting the arm here makes the population splittable from BI regardless of what Petri thinks it
   * exposed. It does NOT make double-ramping safe — ramp ONE leg at a time.
   */
  const pickerSeenRef = useItemSeenBi('package-picker', {
    additionalFields: { billing_cycle_arm: cycleStepOn ? 'variant' : 'control' },
    disabled: cycleStepArm === undefined,
  });

  // Whether the shared subgrid needs a top-up track and a promo-code track AT ALL.
  //
  // Every card has to render every track for the bands to line up, so a track nobody fills still
  // costs a full row gap on every card. At the 46px the frame asks for, the two optional bands
  // put ~92px of dead white between the CTA and "What you get" on a picker where NOBODY can see a
  // top-up — which is every visitor without a paid subscription, i.e. the common case. So the track
  // count is derived from whether any card actually has something to put there.
  const showTopupTrack = useMemo(
    () =>
      hasRealSubscription &&
      (plans ?? []).some((p) =>
        // Same ownership rule the card itself applies (`ownsOption`), and for the same reason: with
        // the cycle unasked the cards are pinned to yearly, so a cadence-sensitive test here reserved
        // no top-up row for a MONTHLY subscriber — and the card below would then have had nowhere to
        // draw the button it correctly offers them.
        p.options.some((o) => ownsOption(subCadenceByPlanId.get(o.agentPlanId), cadence, cycleStepOn) && o.topup != null),
      ),
    [plans, subCadenceByPlanId, cadence, hasRealSubscription, cycleStepOn],
  );
  // Coupons ride on checkout only, so a subscriber (who switches in place) is offered none.
  // Gated on the arm for the same reason `showTopupTrack` is gated at all: a track nobody fills still
  // costs a full subgrid row gap on every card. In the variant the field lives on the step, so
  // leaving this on reserved an empty row across all four cards.
  const showCouponTrack = billingLane === 'live' && !isSubscribed && !cycleStepOn;

  // Read any pending deferred downgrade once the user is known to be subscribed. Cleared when
  // they're not (nothing can be scheduled without a subscription).
  const loadScheduledChange = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/premium/scheduled-change`, { credentials: 'include' });
      const json = (await res.json()) as {
        hasPendingChange?: boolean;
        pendingPlanId?: string | null;
        scheduledChangeDate?: string | null;
        pendingCancellation?: boolean;
        cancellationDate?: string | null;
      };
      setPendingChange(
        json.hasPendingChange
          ? { pendingPlanId: json.pendingPlanId ?? null, scheduledChangeDate: json.scheduledChangeDate ?? null }
          : null,
      );
      setCancellationDate(json.pendingCancellation ? json.cancellationDate ?? null : null);
    } catch {
      setPendingChange(null);
      setCancellationDate(null);
    }
  }, []);
  useEffect(() => {
    if (isSubscribed) void loadScheduledChange();
    else {
      setPendingChange(null);
      setCancellationDate(null);
    }
  }, [isSubscribed, loadScheduledChange]);

  // DEBUG-ONLY: reset the signed-in user back to a fresh (never-purchased) state so a teammate can
  // re-run the picker + checkout flow. Refunds/cancels the caller's own purchases server-side
  // (see POST /api/premium/reset), then reloads premium so the picker reverts to unsubscribed.
  const resetForTesting = useCallback(async (): Promise<ResetSummary> => {
    const res = await fetch(`${API_BASE}/api/premium/reset`, { method: 'POST', credentials: 'include' });
    const json = (await res.json()) as ResetSummary & { ok?: boolean; error?: string; message?: string };
    if (!res.ok || json.ok === false) {
      throw new Error(json.message || json.error || 'Reset failed.');
    }
    await loadPremium();
    return json;
  }, [loadPremium]);

  // Open the confirm dialog for a switch. Classifies upgrade vs downgrade off the picker's prices
  // (for the confirm copy only — the backend re-classifies by Stripe price and is authoritative).
  const openSwitchConfirm = useCallback(
    (plan: PickerPlan, option: PickerOption, cadence: Cadence) => {
      const currentSub = (premium?.subscriptions ?? []).find((s) => s.status === 'ACTIVE' || s.status === 'PAST_DUE');
      const current = findOption(plans, currentSub?.planId);
      const currentAmount = amountFor(current?.option, cadenceOf(currentSub));
      const targetAmount = amountFor(option, cadence);
      let kind: 'upgrade' | 'downgrade' | 'unknown' = 'unknown';
      if (currentAmount != null && targetAmount != null) {
        kind = targetAmount > currentAmount ? 'upgrade' : targetAmount < currentAmount ? 'downgrade' : 'unknown';
      }
      dismissToast();
      setConfirm({
        agentPlanId: option.agentPlanId,
        cadence,
        actionSourceScope: [
          currentSub?.providerSubscriptionId ?? '',
          currentSub?.planId ?? '',
          cadenceOf(currentSub),
        ].join(':'),
        targetPlanName: t(plan.nameKey),
        targetCreditsLabel: t('pricing.monthlyCredits', { credits: option.credits.toLocaleString() }),
        priceLabel:
          targetAmount != null
            ? `${formatMoney(targetAmount, currency)} ${t(cadence === 'yearly' ? 'pricing.perYear' : 'pricing.perMonth')}`
            : '',
        kind,
        currentPlanName: t(current?.plan.nameKey ?? 'pricing.yourCurrentPlan'),
        effectiveDate: formatDate(currentSub?.currentPeriodEnd),
      });
    },
    [plans, premium, currency, t, dismissToast],
  );

  // An SCA hold's progress reported as a load region, because that is what it is: an async step with
  // a start and an outcome. `sca-challenge` opens when handleNextAction is invoked and closes on the
  // verdict; every other stage rides `loadStart` under its own component name, so one query returns
  // the whole ladder.
  //
  // Instrumentation only: `report()` in upgrade-confirmation swallows throws, so nothing here can
  // change a money verdict.
  const scaBi = useGenericBi('packages');
  const reportScaStage = useCallback(
    (subscriptionId?: string, billingLane?: BillingLane): ScaStageReporter => {
      // Per-attempt, because a loadFinish may only close a region that actually OPENED. The paths
      // that never reach Stripe (no secret, no key, bad lane, Stripe.js blocked) still produce a
      // verdict, and finishing `sca-challenge` for those would emit orphan finishes — inflating the
      // region's count and poisoning its duration/success rates with attempts that never ran. Those
      // verdicts are reported as their own `sca-verdict` start instead, so nothing goes unobserved.
      let regionOpen = false;
      return (stage, detail) => {
        // The same stage to app_logs, because the BI events are currently unreadable there — see
        // lib/sca-stage-sink. Both destinations get every stage; neither can throw at this caller.
        postScaStage({ stage, detail, subscriptionId, billingLane });
        const additionalFields = {
          sca_stage: stage,
          ...(detail ? { sca_detail: detail } : {}),
          ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
        };
        if (stage === 'verdict') {
          if (regionOpen) {
            scaBi.logLoadFinish('sca-challenge', { isSuccessful: detail === 'paid' }, { additionalFields });
          } else {
            scaBi.logLoadStart('sca-verdict', { additionalFields });
          }
          return;
        }
        if (stage === 'challenge-invoked') {
          regionOpen = true;
          scaBi.logLoadStart('sca-challenge', { additionalFields });
          return;
        }
        scaBi.logLoadStart(`sca-${stage}`, { additionalFields });
      };
    },
    [scaBi],
  );

  // Execute a confirmed switch: SwitchPlan (card on file, no checkout), then reflect the outcome —
  // an upgrade applies immediately (poll for the new active plan); a downgrade is scheduled (refresh
  // the pending banner).
  //
  // It returns what the attempt SETTLED as, which the escape below depends on: a replay that is
  // declined or blocked again fails no differently from one that goes through as far as the caller can
  // see, so without this the discard could only report that it dispatched a replay — and that reads in
  // BI as a paid retry. The rejection values are `SwitchRejectionAction`'s own, so the ladder's verdict
  // passes through rather than being re-derived here.
  const executeSwitch = useCallback(
    async (c: SwitchConfirm): Promise<SwitchAttempt> => {
      // Keep the review-and-pay dialog mounted through the charge so it reads as a transaction
      // (button → "Processing…"), then close it in `finally` once the outcome/notice is set.
      setSwitchingId(c.agentPlanId);
      dismissToast();
      try {
        // The token for THIS action — the same one again if the user is retrying it.
        const idempotencyKey = switchActionKeys.current.keyFor(
          c.agentPlanId,
          c.cadence,
          c.actionSourceScope,
        );
        const res = await fetch(`${API_BASE}/api/premium/switch-plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ agentPlanId: c.agentPlanId, cadence: c.cadence, idempotencyKey }),
        });
        const json = (await res.json()) as {
          subscriptionId?: string;
          outcome?: SwitchOutcomeStr;
          scheduledChangeDate?: string | null;
          paymentIntentClientSecret?: string | null;
          publishableKey?: string;
          billingLane?: 'live' | 'sandbox';
          error?: string;
          code?: string;
          message?: string;
        };
        // The payment's outcome is UNKNOWN (it may already have succeeded, or still be settling).
        // Per the server contract this must never read as "payment failed" and must never abandon
        // the hold: KEEP the action's key — an exact-key retry replays the same action — and poll.
        const reflectAmbiguousOutcome = () => {
          showToast('muted', t('pricing.notice.finishingUpgrade'));
          pollPremium((p) => activeIds(p).has(c.agentPlanId));
        };
        // Every branch below renders LOCALIZED copy — never `json.message`. The server classifies into
        // `code` precisely so the client never has to render an upstream string, and rendering one put
        // raw gRPC and Jackson text in front of buyers. (`stale_client` still ships authored `message`
        // copy for tabs running a bundle from before the route grew `idempotencyKey`; this bundle
        // always sends one, so it cannot reach that branch.)
        if (!res.ok) {
          // The whole ladder lives in lib/switch-rejection so the KEY RULE is testable, not just the
          // table it reads: only a definitive decline may rotate this action's token, because Stripe
          // replays a cached response for 24h (reuse answers a corrected card with the old decline) and
          // every other rejection may have charged (rotating turns the retry into a second charge).
          //
          // The escape is gated on the LANE, never defaulted to one: voiding is a write against a single
          // Stripe account, and an escape we cannot aim would spend the buyer's click to tell them
          // nothing. Unreachable in practice — the service that raises this precondition is only called
          // after the lane is resolved — which is exactly why it must not carry a guess.
          const lane = json.billingLane;
          return applySwitchRejection(json.code, Boolean(lane), {
            releaseActionKey: () =>
              switchActionKeys.current.release(c.agentPlanId, c.cadence, c.actionSourceScope),
            // Says the payment didn't land and that nothing was charged — true at every throw site — and
            // offers both remedies. Deliberately NOT "your card was declined": see the warning on
            // `declined` in lib/switch-rejection, where two of the four sites are our own failure.
            showDeclined: () => showToast('error', t('pricing.error.paymentDeclined')),
            // An earlier attempt of this same switch is still running: money may be moving, which is the
            // "finishing up" situation rather than an error.
            reflectInFlight: reflectAmbiguousOutcome,
            // An EARLIER hold blocks every action — this one included — until it is finished or canceled,
            // for up to ~23h. Nothing here reached Stripe, so the token is untouched and stays reusable.
            offerHoldEscape: () => setPendingUpgrade({ confirm: c, billingLane: lane as BillingLane }),
            showAppleOwned: () => showToast('error', t('checkout.error.apple-subscription')),
            showGenericFailure: () => showToast('error', t('pricing.error.switchPlan')),
          });
        }
        // The upgrade landed and was charged — reflect it. Shared by the synchronous path and a
        // confirmed (SCA) payment, which converge on the same paid state.
        const reflectUpgradedNow = () => {
          switchActionKeys.current.release(c.agentPlanId, c.cadence, c.actionSourceScope);
          showToast('success', t('pricing.notice.upgradedNow', { planName: c.targetPlanName }));
          setPendingChange(null);
          pollPremium((p) => activeIds(p).has(c.agentPlanId));
          // An immediate upgrade grants credits too — nudge the sidebar/settings readout to poll for
          // them, same as a fresh purchase (see packages.tsx), so it doesn't lag until a manual refresh.
          try {
            window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
          } catch {
            /* no window (SSR) — the picker's own poll still reconciles its view */
          }
        };
        if (json.outcome === 'UPGRADE_REQUIRES_ACTION') {
          // The tier change is HELD in Stripe behind an SCA/3DS challenge — nothing charged, nothing
          // applied yet. The action's key is deliberately KEPT through the challenge: a retry of this
          // exact action replays the same held secret instead of minting a second charge. The whole
          // decision ladder (when to abandon, what counts as canceled) is resolveHeldUpgrade's,
          // pinned by lib/upgrade-confirmation.test.ts; this maps each resolution to its key rule.
          const resolution = await resolveHeldUpgrade(
            json.paymentIntentClientSecret,
            json.publishableKey,
            json.billingLane,
            { onStage: reportScaStage(json.subscriptionId, json.billingLane) },
          );
          if (resolution === 'paid') {
            reflectUpgradedNow();
            return 'applied';
          }
          if (resolution === 'canceled') {
            // Server-CONFIRMED void of the held invoice: nothing was charged, and a fresh action
            // is allowed — this is the one non-paid outcome that rotates the key.
            switchActionKeys.current.release(c.agentPlanId, c.cadence, c.actionSourceScope);
            showToast('muted', t('pricing.notice.upgradeNotCompleted'));
            return 'canceled';
          }
          reflectAmbiguousOutcome();
          return 'unsettled';
        }
        // Landed — the next switch from here is a new action.
        if (json.outcome === 'DOWNGRADE_SCHEDULED') {
          switchActionKeys.current.release(c.agentPlanId, c.cadence, c.actionSourceScope);
          // No transient notice here: loadScheduledChange() surfaces the persistent
          // PendingChangeBanner ("Moving to <plan>… Undo") before the dialog closes, which is the
          // single canonical indicator for a scheduled downgrade. Emitting a green "Scheduled…"
          // banner too just stacked a duplicate of the same information.
          await loadScheduledChange();
          return 'scheduled';
        }
        // UPGRADED_NOW (or unknown outcome): applied immediately. An upgrade also releases any
        // pending downgrade, so clear it and poll until the new plan is the active one.
        reflectUpgradedNow();
        return 'applied';
      } catch {
        // Thrown before any verdict (the fetch itself failed) — the action's key is deliberately KEPT
        // so clicking again reuses it and Stripe collapses the two requests into one charge.
        showToast('error', t('pricing.error.switchPlan'));
        return 'failed';
      } finally {
        setSwitchingId(null);
        setConfirm(null);
        // Leave the billing-cycle step once the switch has settled, whatever it settled as.
        //
        // Two things go wrong if this stays put, and both were reachable. The step has no
        // "Current plan" state and no scheduled-target state — the cards get those from
        // `isCurrentStatus`/`isScheduledTarget`, which is what makes a second purchase of the plan
        // you just bought structurally impossible there. And every settled outcome releases the
        // action's idempotency key, so a second Continue mints a fresh one and issues a genuinely
        // new SwitchPlan for the same plan.
        //
        // It is also the only feedback a scheduled downgrade gets: `DOWNGRADE_SCHEDULED`
        // deliberately raises no toast because `PendingChangeBanner` is meant to be its single
        // canonical indicator — and that banner lives on the cards. Stranded on the step the buyer
        // saw a modal close over an unchanged screen, which reads as "it failed", and pressed again.
        setCycleTarget(null);
      }
    },
    [loadScheduledChange, pollPremium, t, dismissToast, showToast, reportScaStage],
  );

  // Discard the earlier hold that is blocking this action, then replay the action. Both halves matter:
  // voiding alone leaves the buyer back on the picker having to work out that they may press Upgrade
  // again, which is most of the trap. The key is deliberately untouched — the rejected request never
  // reached Stripe, so the same key is still this action's key and the replay must reuse it.
  const discardHoldAndRetry = useCallback(async () => {
    const blocked = pendingUpgrade;
    if (!blocked) return;
    scaBi.logModalActionBi({ page_name: 'packages', modal_name: 'pending_upgrade', action: 'discard' });
    dismissToast();
    // The orchestration is lib/pending-upgrade-escape's, so its two money-relevant rules are unit-tested
    // rather than pinned as source text: an UNCONFIRMED void never replays (it may mean the buyer was
    // just charged), and two presses cannot both run (`disabled` only bites once React commits).
    const result = await discardAndRetry<SwitchAttempt>(discardingHoldRef.current, {
      abandon: () => abandonHeldUpgrade(blocked.billingLane),
      replay: () => executeSwitch(blocked.confirm),
      resync: () => {
        void loadPremium();
        void loadScheduledChange();
      },
      notifyUnresolved: () => showToast('muted', t('pricing.notice.pendingUpgradeUnresolved')),
      // Before the replay, so a repeat rejection re-opens the modal rather than being swallowed.
      closeEscape: () => setPendingUpgrade(null),
      setBusy: (busy) => {
        discardingHoldRef.current = busy;
        setDiscardingHold(busy);
      },
    });
    // The OUTCOME, not just the click. This flow exists because the trap was measurable; without this
    // you cannot separate "voided and the retry went through" from "voided and declined again" from
    // "we could not confirm the void and they walked away".
    //
    // `isSuccessful` is the buyer getting the plan they came for, so it needs the REPLAY's verdict:
    // reaching the replay at all is not it. `replay_outcome` keeps the rest legible — a decline and a
    // still-settling payment are both non-success but they mean opposite things about the card.
    if (result.path !== 'busy') {
      scaBi.logLoadFinish(
        'pending-upgrade-discard',
        { isSuccessful: result.path === 'replayed' && result.replay === 'applied' },
        {
          additionalFields: {
            discard_outcome: result.path,
            replay_outcome: result.path === 'replayed' ? result.replay : undefined,
          },
        },
      );
    }
  }, [pendingUpgrade, executeSwitch, loadPremium, loadScheduledChange, dismissToast, showToast, t, scaBi]);

  // When the upgrade review screen opens, fetch the card on file so it can show "Visa •••• 4242".
  // Upgrade only (a downgrade is deferred, not a charge). Best-effort: on any failure the modal
  // falls back to generic "card on file" copy. Cleared when the dialog closes / on a downgrade.
  useEffect(() => {
    if (!confirm || confirm.kind === 'downgrade') {
      setCardOnFile(null);
      return;
    }
    let cancelled = false;
    setCardOnFile(null);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/premium/payment-method`, { credentials: 'include' });
        const json = (await res.json()) as CardOnFile;
        if (!cancelled) setCardOnFile(json);
      } catch {
        if (!cancelled) setCardOnFile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [confirm]);

  // Undo a pending deferred downgrade OR a pending cancellation — the backend clears whichever is
  // set, so we clear both client-side markers on success (a scheduled cancel is a "Keep my plan").
  const undoScheduledChange = useCallback(async () => {
    setUndoing(true);
    dismissToast();
    try {
      const res = await fetch(`${API_BASE}/api/premium/cancel-scheduled-change`, { method: 'POST', credentials: 'include' });
      const json = (await res.json()) as { canceled?: boolean; error?: string; code?: string };
      if (res.ok && json.canceled) {
        setPendingChange(null);
        setCancellationDate(null);
        showToast('success', t('pricing.notice.changeCanceled'));
      } else if (!res.ok) {
        showToast(
          'error',
          json.code === APPLE_SUBSCRIPTION_CODE ? t('checkout.error.apple-subscription') : t('pricing.error.cancelChange'),
        );
      } else {
        await loadScheduledChange(); // no-op cancel (already gone) — resync
      }
    } catch {
      showToast('error', t('pricing.error.cancelChange'));
    } finally {
      setUndoing(false);
    }
  }, [loadScheduledChange, t, dismissToast, showToast]);

  // Cancel the active subscription at period end (Cancel plan in SubscriptionLine, under the cards). The
  // user keeps the plan + credits until the current period ends; the pending cancellation then
  // surfaces as "Cancels on <date>" and can be undone via undoScheduledChange ("Keep my plan").
  const cancelSubscription = useCallback(async () => {
    setCanceling(true);
    dismissToast();
    try {
      const res = await fetch(`${API_BASE}/api/premium/cancel-subscription`, { method: 'POST', credentials: 'include' });
      const json = (await res.json()) as { effectiveDate?: string | null; error?: string; code?: string };
      if (!res.ok) {
        // Translated copy only — the route deliberately returns no presentable message, and its
        // `error` is a code. Echoing either put a raw gRPC status string in this toast.
        showToast(
          'error',
          json.code === APPLE_SUBSCRIPTION_CODE ? t('checkout.error.apple-subscription') : t('settings.plan.cancelError'),
        );
        return;
      }
      const when = formatDate(json.effectiveDate);
      showToast('success', when ? t('settings.plan.willEndOn', { date: when }) : t('settings.plan.willEndPeriod'));
      await loadScheduledChange();
      setPremium(await loadPremium());
    } catch {
      showToast('error', t('settings.plan.cancelError'));
    } finally {
      setCanceling(false);
    }
  }, [loadScheduledChange, loadPremium, t, dismissToast, showToast]);

  // At most ONE transient banner shows above the cards — never a stack. An error outranks a
  // just-completed action (the in-app switch), which outranks the Stripe checkout-return status.
  // A scheduled downgrade is deliberately NOT surfaced here: it has its own persistent
  // PendingChangeBanner (with Undo) below, so a green "Scheduled…" notice would just duplicate it.
  return (
    // `sym-pricing` scopes the Symphony design-system tokens (--sym-ink / --sym-bg-* / --sym-radius-*)
    // to the picker so it renders its light marketing palette on EVERY mount — those tokens are
    // otherwise defined only under [data-brand="symphony"] (the /app mount), leaving them undefined
    // on the Orion-design mounts (/orion-web, /symphony-web) this picker also serves. See globals.css.
    <div ref={paneRef} className="sym-pricing" style={{ width: '100%', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      {/* Cards are laid out with a container-query grid. Once the pane is wide enough for all
          cards in one row, each card becomes a `subgrid` spanning shared row tracks
          (name+tagline / price+credits / CTA / top-up / what's-included / promo code), so those bands
          line up at the same Y across every card no matter how long each card's tagline or feature
          list is. Below that width the cards fall back to a plain stacked/2-up flex column. Container
          queries (not viewport media queries) so the sidebar width is accounted for correctly.

          Sizes are the frame's OWN literals, not a scaled copy of them. They used to be scaled by
          ~0.7 against an older frame whose cards were 415 wide; the current one (Figma 15601:339790)
          draws 380px cards in a 1180 row, and this pane fits 1146 — within 3% — so there is nothing
          left to scale away and the downscale was simply making the picker half-size. The headline
          was the worst of it: 52px against the frame's 90, where the marketing pricing page renders
          the same words at 99. If you re-measure the frame, take its numbers as they are. */}
      <style>{`
        .sym-plans-ct { container-type: inline-size; }
        .sym-plans {
          display: grid;
          column-gap: 20px;
          /* The row gap has a FLOOR, not a preference: the recommended card's plate rises
             RECOMMENDED_BANNER_RISE out of its own top edge, so a smaller gap lets it paint over the
             card stacked above it in the one-column layout.
             It moved 30 -> 56 with the plate's own rise (22 -> 44); they go up together. */
          row-gap: 56px;
          max-width: 1320px;
          margin: clamp(32px, 5.8vh, 70px) auto 0;
          justify-content: center;
          grid-template-columns: minmax(0, 380px);
        }
        .sym-card { display: flex; flex-direction: column; gap: 24px; }
        /* Every band is bounded by the CARD, not by its own content. A flex/grid child's automatic
           minimum size is its min-content width, and the price headline is a nowrap 52px run — so
           without this the price pushed its band, and the whole card's column, wider than the track
           and spilled over the neighbouring card (Vietnam's seven-digit dong price). It also has to
           hold for the fit measurement to mean anything: an unbounded row reports the width it
           overflowed to, so the headline would measure itself as fitting and never shrink. */
        .sym-card > * { min-width: 0; }
        /* Three resting states, which is the frame's own hierarchy: the plated Recommended tier wears
           the accent, the pushed tier without its plate wears the solid neutral, and every other card
           is an OUTLINE. The frame draws Max and Basic outlined; filling all four with the neutral —
           which is what this used to do — made four equally loud buttons and left the recommended one
           with nowhere louder to go.

           ONE hover, for every CTA on the screen: the solid fill. Three resting states collapse to a
           single hovered one, so pointing at any of these buttons means the same thing wherever it
           lands — including the accent ones, which get no softer treatment of their own. The accent
           says "this is the tier we push"; it is not a claim about what hovering does.

           It fires on the BUTTON, never the card. A card-wide trigger lit the action from anywhere on
           the card, which reads as a promise the rest of the card cannot keep: the price, the credits
           and the feature list are not buttons.

           The border is present in EVERY state and only its COLOUR changes. These CTAs share a
           subgrid row across the cards, so a border that appeared on hover would shift all four. */
        .sym-cta {
          background: transparent;
          color: ${INK};
          /* A HAIRLINE. Measured off the frame's own 2x render: the stroke is 2 device pixels on a
             card that renders at 756 for a 380 frame card, i.e. 1px. It shipped at 2 and read as a
             heavy box rather than an outlined button. A hairline does not scale with the pane. */
          border: 1px solid ${INK};
          transition: background .15s ease, border-color .15s ease, color .15s ease, opacity .15s ease;
        }
        /* Declared rather than left to the UA, which on an accent fill in Safari is close to
           invisible. An outline sitting OFF the button also stays legible over the hovered fill,
           since hover and focus land together for a pointer user who clicks. */
        .sym-cta:focus-visible, .sym-topup:focus-visible {
          outline: 2px solid ${INK}; outline-offset: 2px;
        }
        /* Descendant-qualified so it outranks the two resting rules below on specificity rather than
           on source order — an accent CTA at rest matches at the same weight as a bare .sym-cta:hover
           would, and reordering the block would silently decide which wins. */
        .sym-card .sym-cta:not(:disabled):hover,
        .sym-topup:not(:disabled):hover {
          background: ${CTA_FILL}; border-color: ${CTA_FILL}; color: var(--ink-on-fill);
        }
        /* The pushed tier with its plate suppressed — the buyer holds it already, or holds something
           bigger (see outranksRecommendedTier). Solid keeps it a step above the outlined cards
           without re-running the accent at someone the plate was deliberately taken away from. It is
           therefore the one CTA whose hover is a no-op: it already wears what hover would give it. */
        .sym-card-pushed .sym-cta:not(:disabled) {
          background: ${CTA_FILL}; border-color: ${CTA_FILL}; color: var(--ink-on-fill);
        }
        /* The plated Recommended tier, and the current card's "Add credits": the accent at REST, as
           the frame draws both. Rest only — the hover above takes them to the solid fill like every
           other CTA. */
        .sym-card-recommended .sym-cta:not(:disabled) {
          background: ${ACCENT_SOLID}; border-color: ${ACCENT_SOLID}; color: ${ACCENT_INK};
        }
        /* SubscriptionLine's link: fine-print ink at rest, not the DS text button's full semibold ink,
           so cancelling stays findable without competing with the cards. Two classes beat the
           module's colour class; the hover selector out-ranks the module's own hover. */
        .sym-sub-line .sym-sub-link {
          font-size: 13px; font-weight: 400; color: rgba(var(--text-label-rgb), 0.55);
          text-decoration-color: rgba(var(--text-label-rgb), 0.3); text-underline-offset: 3px;
        }
        .sym-sub-line .sym-sub-link:hover:not(:disabled):not([aria-disabled='true']) {
          color: rgba(var(--text-label-rgb), 0.85); text-decoration-color: currentColor;
        }
        .sym-topup { background: ${ACCENT_SOLID}; color: ${ACCENT_INK}; transition: background .15s ease, color .15s ease, opacity .15s ease; }
        @container (min-width: 700px) {
          .sym-plans { grid-template-columns: repeat(2, minmax(0, 380px)); }
        }
        @container (min-width: 1000px) {
          .sym-plans {
            grid-template-columns: repeat(var(--sym-n, 4), minmax(0, 380px));
            grid-template-rows: repeat(var(--sym-rows, 6), auto);
          }
          .sym-card {
            display: grid;
            /* The implicit column would be auto-sized, i.e. free to grow to its widest content and
               overflow the track. Bound it to the card. */
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: subgrid;
            grid-row: 1 / -1;
          }
        }
      `}</style>
      {/* The billing-cycle step (variant arm) REPLACES the plan cards rather than opening over them:
          the buyer has answered the plan question and is now answering the price one, and leaving the
          cards behind a scrim would keep four prices on screen that are no longer the offer. The
          disclaimer below stays put on both screens — it qualifies whichever numbers are showing. */}
      {/* ONE SCREEN, ending on the disclaimer.

          The picker opens showing the whole offer and its footnote, with the blue band starting just
          past the fold — so the first thing a buyer does with the wheel reveals that there is more,
          rather than the band peeking in under the cards and competing with them for the first look.

          `min-height` is measured rather than expressed in `dvh`: the content does not start at the
          top of the viewport (there is a rail, the pane's own 72px of padding, and on narrow an app
          banner above it), and the pane is the scroll container, not the document. So the height that
          matters is the SCROLLER's, minus wherever this wrapper begins inside it — see the effect.

          It is a floor, never a cap: when the cards are taller than the screen this does nothing and
          the disclaimer simply falls below the fold, which is the honest outcome — there is no height
          at which all of it fits. What the floor guarantees in every case is the other half of the
          ask: the blue band is never visible before the first scroll. */}
      <div ref={foldRef} style={{ display: 'flex', flexDirection: 'column', boxSizing: 'border-box', paddingBottom: FOLD_BREATHING_ROOM }}>
      {cycleTarget ? (
        <BillingCycleStep
          planName={t(cycleTarget.plan.nameKey)}
          planKey={cycleTarget.plan.key}
          option={cycleTarget.option}
          currency={currency}
          featureKeys={cycleTarget.option.featureKeys}
          isSwitch={hasRealSubscription}
          busy={disabled || switchingId !== null}
          couponsEnabled={billingLane === 'live'}
          onBack={() => setCycleTarget(null)}
          onContinue={(chosen, couponCode) => {
            const { plan, option } = cycleTarget;
            // The cards' own CTA rule, moved one screen later: an existing subscriber changes plans
            // in place (SwitchPlan, card on file), a first purchase goes through Stripe Checkout.
            // Read off `hasRealSubscription`, not `activePlanIds`, so the optimistic post-purchase
            // window — a seeded plan whose webhook grant has not landed — still checks out rather
            // than trying to switch a subscription that does not exist server-side yet.
            if (hasRealSubscription) return openSwitchConfirm(plan, option, chosen);
            return onCheckout(option.agentPlanId, chosen, plan.key, couponCode);
          }}
        />
      ) : (
        <div ref={pickerSeenRef}>
        <header style={{ textAlign: 'center', marginBottom: 'clamp(24px, 4vh, 48px)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <h1
            style={{
              margin: 0,
              fontFamily: ARCHIVO,
              fontWeight: 700,
              // Fourth pass on this number. 52 (a hair over half the frame) read flat next to the
              // marketing pricing page, which sets the same two lines at 99; the frame's own 90 was
              // then a touch too big for a pane that also carries a rail; 72 was the middle; 64 is
              // where it settled, by request. The size is a product decision, not a measurement —
              // do not "restore" it to the frame.
              fontSize: 'clamp(32px, 3.8vw, 64px)',
              lineHeight: 0.99,
              letterSpacing: '-0.04em',
              color: HEADLINE_INK,
              textWrap: 'balance',
            }}
          >
            {isSubscribed ? (
              t('pricing.header.titleUpgrade')
            ) : (
              <>
                {t('pricing.header.title')}
                {/* -0.06em, the frame's tracking for this run (-6px on its 100px serif). It used to be
                    -0.03, i.e. half as tight, which is what made the serif read loose beside the
                    Archivo half — the frame sets the two at nearly the same ratio, not the serif at
                    half. The Archivo run's own -0.04em already matches the frame's -4px on 90px. */}
                {/* The serif run is the LARGER of the two — the frame sets 100 against the Archivo
                    half's 90, so it carries its own em ratio rather than inheriting the size — and it
                    is a BLOCK, because the frame's headline is two lines with a hard break between
                    them. Left inline it fits on one line at this size on a wide pane, which reads as
                    a single long sentence rather than the two-beat headline the frame draws. */}
                <span style={{ display: 'block', fontFamily: SERIF, fontWeight: 400, fontSize: '1.11em', letterSpacing: '-0.06em' }}>
                  {t('pricing.header.titleAccent')}
                </span>
              </>
            )}
          </h1>
          <TrustLine />
        </header>

        {/* A toggle between two things needs two things. In a monthly-only market (see
            `pricedCadences`) it would offer a cadence with no price behind it on every card, so it is
            not rendered at all — the cards then simply show monthly prices, which is the whole offer.
            Still rendered while the catalog loads (`pricedCadences === null`) so the header does not
            reflow once plans arrive.

            The variant arm has no toggle: the cycle is asked on the next screen. While the arm is
            still UNRESOLVED it is rendered but HIDDEN, holding its exact height — a control user then
            sees it appear in place with no reflow, and a variant user never sees it at all. Painting
            it for real first and pulling it once the gate answers would both jolt the header and hand
            a fast clicker a cadence control that is about to stop existing. `visibility`, not
            `display`, is what keeps the space, and the hidden copy is the real component so the two
            can never disagree about how tall it is. */}
        {(!cadencesPriced || cadencesPriced.yearly) && cycleStepArm !== true && (
          <div
            aria-hidden={cycleStepArm === undefined}
            style={cycleStepArm === undefined ? { visibility: 'hidden' } : undefined}
          >
            <BillingToggle cadence={cadence} onChange={setSelectedCadence} />
          </div>
        )}

        {pendingChange && (
          <PendingChangeBanner
            planName={t(findOption(plans, pendingChange.pendingPlanId)?.plan.nameKey ?? 'pricing.yourNewPlan')}
            date={formatDate(pendingChange.scheduledChangeDate)}
            onUndo={undoScheduledChange}
            undoing={undoing}
          />
        )}

        {loadError ? (
          <Banner tone="error">{t('pricing.error.loadPlansPrefix', { error: loadError })}</Banner>
        ) : plans === null || !premiumLoaded || cycleStepArm === undefined ? (
          // Wait for plans, the first premium fetch AND the billing-cycle arm before rendering
          // cards. The first two are the long-standing reason: otherwise a subscribed user briefly
          // sees the not-subscribed layout (every card offering a first purchase, none marked as
          // theirs) until premium loads. premiumLoaded
          // flips on success OR error (error → graceful not-subscribed).
          //
          // The arm joins them because a card rendered before the gate answers is a card that SPENDS
          // MONEY on the wrong flow: `onPickCycle` is undefined until `cycleStepOn` is true, so a
          // variant buyer clicking "Get started" in that window fell straight through to a one-click
          // yearly checkout, never having been asked the cycle question this arm exists to ask —
          // logged as variant, behaving as control. Holding the toggle alone (which is all that used
          // to happen here) protected the control nobody buys with and left the one they do.
          <p style={{ textAlign: 'center', marginTop: 40, fontFamily: MADEFOR_TEXT, color: 'rgba(var(--text-label-rgb), 0.5)' }}>
            {t('pricing.loadingPlans')}
          </p>
        ) : plans.length === 0 ? (
          <Banner tone="muted">{t('pricing.noPlans')}</Banner>
        ) : (
          // The cadence as a `component_route` segment, so a click on a card reports WHICH price the
          // buyer was looking at when they clicked it. It has to sit here rather than on the click:
          // `clickCta` takes no route, the library composes it from this provider chain, and a
          // provider only reaches loggers created BELOW it — which is why `PlanCard` calls
          // `useGenericBi` itself instead of taking a logger down as a prop.
          // In the variant the cards carry no cadence at all (that is the arm), so it reports the
          // absence rather than guessing one.
          <GenericEventsRouteProvider value={cycleStepOn ? 'cycle-unasked' : cadence}>
          <div className="sym-plans-ct">
            <div
              className="sym-plans"
              style={
                {
                  ['--sym-n' as string]: plans.length,
                  ['--sym-rows' as string]: 4 + (showTopupTrack ? 1 : 0) + (showCouponTrack ? 1 : 0),
                } as CSSProperties
              }
            >
              {(displayPlans ?? plans).map((plan) => (
                <PlanCard
                  key={plan.key}
                  plan={plan}
                  showTopupTrack={showTopupTrack}
                  showCouponTrack={showCouponTrack}
                  cadence={cadence}
                  currency={currency}
                  pendingId={pendingId}
                  busy={disabled || switchingId !== null}
                  activePlanIds={activePlanIds}
                  outranksRecommended={outranksRecommended}
                  saleActive={saleActive && personalSaleAppliesToPlan(plan.key)}
                  isTopTier={plan.key === topTierPlanKey}
                  hasRealSubscription={hasRealSubscription}
                  subCadenceByPlanId={subCadenceByPlanId}
                  switchingId={switchingId}
                  pendingPlanId={pendingChange?.pendingPlanId ?? null}
                  pendingDate={formatDate(pendingChange?.scheduledChangeDate)}
                  cancellationDate={formatDate(cancellationDate)}
                  couponsEnabled={billingLane === 'live' && !cycleStepOn}
                  cycleUnasked={cycleStepOn}
                  onPickCycle={
                    cycleStepOn
                      ? (p, o) => {
                          rememberCardsScroll();
                          setCycleTarget({ plan: p, option: o });
                        }
                      : undefined
                  }
                  onCheckout={onCheckout}
                  onTopup={onTopup}
                  onSwitch={openSwitchConfirm}
                />
              ))}
            </div>
          </div>
          {hasRealSubscription && paidSub && (
            <SubscriptionLine
              planName={t(findOption(plans, paidSub.planId)?.plan.nameKey ?? 'settings.plan.paid')}
              cadence={cadenceOf(paidSub)}
              renewsOn={paidSub.status === 'ACTIVE' ? formatDate(paidSub.currentPeriodEnd) : ''}
              cancellationDate={formatDate(cancellationDate)}
              canceling={canceling}
              keeping={undoing}
              onCancel={cancelSubscription}
              onKeepPlan={undoScheduledChange}
            />
          )}
          </GenericEventsRouteProvider>
        )}
        </div>
      )}

      {/* Disclaimer sits at the FOLD — see the `foldRef` effect. It is `margin-top: auto` inside a
          wrapper that is exactly one screen tall, so it lands on the bottom edge of the first view
          however short the cards are, and the blue band begins immediately under it.

          The rest of this note is why the paragraph looks the way it does. Not pinned to the pane's
          bottom edge: the app-wide
          FloatingChat composer floats over the bottom of the pane, so a bottom-pinned footer landed
          under it. marginTop gives it clear air below the picker; the pane's own bottom padding
          (packages.tsx) keeps it clear of the composer when the content is tall enough to scroll.

          Held until /api/plans has resolved the geo. Rendering it earlier is not neutral: with `geo`
          still undefined the tax-silent variant paints first (see showsTaxInclusivePrices, which fails
          that way deliberately), so a US or Canadian buyer watched the paragraph GROW by two sentences
          when the catalog landed. It also has nothing to disclaim before then — there are no prices on
          screen yet, and an /api/plans failure shows a banner instead of a picker.

          The frame sets this at 16px Medium in the full ink, which is a THIRD place the redesign
          deliberately does not follow it: at that weight and contrast a legal paragraph competes with
          the cards it qualifies. It stays quiet — 14px regular at 0.6 alpha.

          `marginTop: auto` is back, but it now resolves against the one-screen wrapper rather than
          against a pane of unknown height, which is what makes it land ON the fold instead of at the
          bottom of everything. */}
      {geo !== undefined && (
        // Under SubscriptionLine the clamp is sized for clearing the cards, and leaves the line
        // floating far above the paragraph — so the gap is fixed and tighter there.
        <div style={{ marginTop: 'auto', paddingTop: hasRealSubscription && paidSub ? 24 : 'clamp(32px, 6.6vh, 80px)' }}>
          <p
            style={{
              maxWidth: 1009,
              margin: '0 auto',
              textAlign: 'center',
              fontFamily: MADEFOR_TEXT,
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '-0.014em',
              color: 'rgba(var(--text-label-rgb), 0.6)',
            }}
          >
            {t(showsTaxInclusivePrices(geo) ? 'pricing.disclaimer.taxInclusive' : 'pricing.disclaimer')}
          </p>
        </div>
      )}
      </div>

      {/* What the cards cannot say, because each of them is busy being different from the other two:
          the floor every plan shares, and the six questions a buyer asks before paying.

          The CARDS screen only, and the condition is the same one the cards themselves render under.
          The billing-cycle step is a focused mid-purchase screen — the frame draws neither block on
          it, and a buyer who has already picked a plan is being asked one question, not sold to. */}
      {!cycleTarget && (
        <>
          <EveryPlanStacked />
          <PricingFaq />
        </>
      )}

      {/* Debug affordance (allowlisted callers only — the server said so on /api/plans; everyone else
          renders no debug chrome at all): a very subtle corner dot that opens a panel with this user's
          premium/credits state + the raw API payloads. Portaled to <body> so it (and the panel)
          render ABOVE the app-wide FloatingChat composer — inside the picker's own stacking context
          the composer's scrim painted over the dot (hiding it) while its pointer-events:none let
          clicks fall through, leaving it clickable-but-invisible. */}
      {canPreview &&
        mounted &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Debug info"
              title="Debug info"
              onClick={() => setDebugOpen(true)}
              style={{
                position: 'fixed',
                right: 10,
                bottom: 10,
                width: 12,
                height: 12,
                padding: 0,
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(var(--hairline-rgb), 0.18)',
                cursor: 'pointer',
                zIndex: 1300,
              }}
            />
            {debugOpen && (
              <DebugPanel
                premium={premium}
                premiumError={premiumError}
                plans={plans}
                currency={currency}
                activePlanIds={activePlanIds}
                countryPreview={countryPreview}
                onCountryPreview={(code) => {
                  writeCountryPreview(code);
                  setCountryPreview(code);
                }}
                onReset={resetForTesting}
                onClose={() => setDebugOpen(false)}
              />
            )}
          </>,
          document.body,
        )}
      {confirm && (
        <SwitchConfirmModal
          confirm={confirm}
          card={cardOnFile}
          processing={switchingId === confirm.agentPlanId}
          onConfirm={() => executeSwitch(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
      {pendingUpgrade && (
        <PendingUpgradeModal
          discarding={discardingHold}
          onDiscard={discardHoldAndRetry}
          // Two exits, logged apart: pressing "Not now" is a decision about the hold, while Escape / the
          // scrim / the ✕ are a stray dismissal. On a dialog whose whole job is to be TAKEN, conflating
          // them would hide whether buyers are declining the escape or missing it.
          onNotNow={() => {
            scaBi.logModalActionBi({ page_name: 'packages', modal_name: 'pending_upgrade', action: 'not_now' });
            setPendingUpgrade(null);
          }}
          onClose={() => {
            scaBi.logModalActionBi({ page_name: 'packages', modal_name: 'pending_upgrade', action: 'close' });
            setPendingUpgrade(null);
          }}
        />
      )}
      {toast && <Toast toast={toast} onClose={dismissToast} onExited={() => setToast(null)} />}
    </div>
  );
}

/**
 * The way out of a blocked upgrade. An earlier held upgrade refuses every further plan change until it
 * is finished or canceled, and Stripe holds it for ~23h — so without this the buyer's every retry is
 * answered with a precondition they cannot see, act on, or wait out.
 *
 * It offers ONE action, and that action is both halves: void the hold and replay the switch. "Discard"
 * on its own would leave them back on the picker having to guess that pressing Upgrade again is now
 * allowed, which is most of what makes the trap a trap.
 */
function PendingUpgradeModal({
  discarding,
  onDiscard,
  onNotNow,
  onClose,
}: {
  discarding: boolean;
  onDiscard: () => void;
  onNotNow: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // The impression, so the escape has a denominator. This whole flow exists because we could measure
  // the trap (14 holds, 0 paid); shipping the way out with only its click instrumented would make
  // "offered 40 times, taken 3" indistinguishable from "never offered". Nothing is fetched on open, so
  // it is a bare loadFinish — the same best-effort impression the app banner reports.
  const { logLoadFinish } = useGenericBi('packages');
  useEffect(() => {
    logLoadFinish('pending-upgrade-modal', { isSuccessful: true });
    // Once per appearance. logLoadFinish is a fresh ref each render; excluded so this can't re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal
      open
      // Escape, the scrim and the ✕ all route through onClose, so a non-dismissible modal must not
      // still DRAW a close button — an affordance that does nothing reads as a frozen dialog. The wait
      // itself is bounded by ABANDON_TIMEOUT_MS, so this state is short as well as honest.
      onClose={discarding ? () => undefined : onClose}
      hideClose={discarding}
      labelledBy="pending-upgrade-title"
      width={460}
      maxHeightPx={650}
      scrollBody
      cardClassName="sym-picker-modal"
      title={
        <h2
          id="pending-upgrade-title"
          style={{ margin: '0 0 10px', fontFamily: ARCHIVO, fontWeight: 700, fontSize: 22, letterSpacing: '-0.02em', color: INK }}
        >
          {t('pricing.pendingUpgrade.title')}
        </h2>
      }
      footer={
        <>
          <Button variant="text" onClick={onNotNow} disabled={discarding}>
            {t('pricing.pendingUpgrade.notNow')}
          </Button>
          <Button onClick={onDiscard} disabled={discarding} loading={discarding}>
            {discarding ? t('pricing.pendingUpgrade.discarding') : t('pricing.pendingUpgrade.discard')}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, fontFamily: MADEFOR_TEXT, fontSize: 14, lineHeight: 1.5, color: INK }}>
        {t('pricing.pendingUpgrade.body')}
      </p>
    </Modal>
  );
}

// A single overlay toast. Fixed to the viewport so it never shifts the picker; fades + slides in on
// mount and, once `leaving`, back out before the parent unmounts it (onExited). Click to dismiss.
function Toast({ toast, onClose, onExited }: { toast: ToastState; onClose: () => void; onExited: () => void }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);
  // Fallback in case the exit transitionend doesn't fire (e.g. dismissed before it finished entering).
  useEffect(() => {
    if (!toast.leaving) return;
    const id = setTimeout(onExited, 320);
    return () => clearTimeout(id);
  }, [toast.leaving, onExited]);

  const accent = { success: 'var(--ds-green-success-600)', error: 'var(--ds-fill-destructive-hover)', muted: 'var(--ds-text-secondary)' }[toast.tone];
  const visible = entered && !toast.leaving;
  return (
    <div
      style={{
        position: 'fixed',
        insetInlineStart: 0,
        insetInlineEnd: 0,
        bottom: 28,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 16px',
        pointerEvents: 'none',
        zIndex: 1200,
      }}
    >
      <div
        role={toast.tone === 'error' ? 'alert' : 'status'}
        aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
        onClick={onClose}
        onTransitionEnd={() => {
          if (toast.leaving) onExited();
        }}
        style={{
          pointerEvents: 'auto',
          cursor: 'pointer',
          maxWidth: 'min(92vw, 460px)',
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          padding: '12px 18px',
          background: 'var(--ds-surface-sunken)',
          border: '1px solid rgba(var(--hairline-rgb), 0.1)',
          borderRadius: 'var(--sym-radius-container)',
          boxShadow: '0 14px 40px -8px rgba(var(--ds-black-rgb), 0.28)',
          fontFamily: MADEFOR_TEXT,
          fontSize: 13.5,
          lineHeight: 1.4,
          color: INK,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(14px)',
          transition: 'opacity 0.24s ease, transform 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <span
          aria-hidden
          style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: accent }}
        />
        <span>{toast.text}</span>
      </div>
    </div>
  );
}

/**
 * The `buy_plan` 198:10, built where the plan AND the cadence are both known.
 *
 * `component_name` used to be the AgentPlan id: a GUID, unreadable in a BI table and a different
 * value per environment. It now carries the CARD KEY (`basic` / `pro` / `max`).
 *
 * The key, NOT the display name. A name resolved through `t()` is translated, so the same plan would
 * arrive as "Max" from one buyer and "Máximo" from another and split into as many rows as we have
 * locales — 37 of them. The key is the name in the only form that survives the trip.
 *
 * The id is not dropped, it moves to `plan_id`. That matters: Max and Max+ are two purchasable
 * options on ONE card and therefore share a key, so the id is what still tells them apart.
 */
const buyPlanBi = (plan: PickerPlan, option: PickerOption, cadence: Cadence, attribution?: PurchaseAttribution) => ({
  componentName: plan.key,
  additionalFields: {
    plan_id: option.agentPlanId,
    plan_name: plan.key,
    cadence,
    credits: String(option.credits),
    // `buy_plan` only (docs/compliance/bi.md §2): what sent the buyer, so the purchase step splits by
    // entry point the way the picker view does. Undefined values are dropped by the BI layer.
    ...(attribution ? { origin: attribution.origin, feature: attribution.feature } : {}),
  },
});

function BillingToggle({ cadence, onChange }: { cadence: Cadence; onChange: (c: Cadence) => void }) {
  const { t } = useTranslation();
  // The toggle reported NOTHING — flipping Monthly/Yearly changed every price on the page and left no
  // 198:10 behind, so the picker's most-used control was invisible in BI. Deliberately the same
  // `select_billing_cycle` name the variant's step fires, so one query covers the choice in both arms
  // and the only thing that differs is where it was made (`component_route`).
  const { logPageClickBi } = useGenericBi('packages');
  const segment = (value: Cadence, label: ReactNode) => {
    const active = cadence === value;
    return (
      <button
        type="button"
        onClick={() => {
          // Re-picking the cadence you are already on is a real click, but not a real choice — it
          // would inflate the very ratio this event exists to measure.
          if (value !== cadence) {
            logPageClickBi(
              { page_name: 'packages', button_name: 'select_billing_cycle' },
              { componentName: value, additionalFields: { cadence: value, chosen_at: 'toggle' } },
            );
          }
          onChange(value);
        }}
        aria-pressed={active}
        style={{
          appearance: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '14px 15px',
          borderRadius: 'var(--sym-radius-button)',
          background: active ? 'var(--ds-control-fill-on-default)' : 'transparent',
          color: active ? 'var(--ink-on-fill)' : 'var(--ds-text-primary)',
          fontFamily: ARCHIVO,
          fontSize: 20,
          fontWeight: 'var(--sym-weight-medium)',
          letterSpacing: '-0.02em',
          lineHeight: 1,
          transition: 'background 0.15s ease, color 0.15s ease',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    );
  };
  // Yearly first, in a filled grey pill — NOT the frame's Monthly-first outlined one. That version
  // was tried and reverted by request, along with the stacked headline above.
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          paddingBlock: '4px',
          paddingInline: '6px 4px',
          borderRadius: 'var(--sym-radius-button)',
          background: 'var(--sym-bg-neutral-medium)',
        }}
      >
        {segment('yearly', t('pricing.billing.yearly'))}
        {segment('monthly', t('pricing.billing.monthly'))}
      </div>
    </div>
  );
}

// The paid subscription's management line, under the cards: plan, cycle and renewal date, then
// "Cancel plan". It replaced a ⋯ menu on the current-plan card, which is drawn only while the cadence
// on screen is the one the buyer owns — so a monthly subscriber looking at the yearly column had no
// cancel path. This line does not depend on the toggle. Cancel ends the plan at period end, and asks
// once before it fires; once scheduled, the line offers "Keep my plan" (undo) instead.
function SubscriptionLine({
  planName,
  cadence,
  renewsOn,
  cancellationDate,
  canceling,
  keeping,
  onCancel,
  onKeepPlan,
}: {
  planName: string;
  cadence: Cadence;
  /** Formatted renewal date, or '' when the plan is not set to renew (past due, or no date). */
  renewsOn: string;
  /** Formatted date a pending cancel-at-period-end lapses, or '' when none is scheduled. */
  cancellationDate: string;
  canceling: boolean;
  keeping: boolean;
  onCancel: () => void;
  onKeepPlan: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  const click = (button_name: string, action: () => void) => () => {
    logPageClickBi({ page_name: 'packages', button_name }, { additionalFields: { cadence } });
    action();
  };
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [cancellationDate]);
  const scheduled = cancellationDate !== '';
  const cadenceLabel = t(cadence === 'yearly' ? 'settings.plan.yearly' : 'settings.plan.monthly');
  const status = scheduled
    ? t('settings.plan.cancelsOn', { date: cancellationDate })
    : renewsOn
      ? t('settings.plan.renews', { cadence: cadenceLabel, date: renewsOn })
      : cadenceLabel;
  const text: CSSProperties = {
    margin: 0,
    fontFamily: MADEFOR_TEXT,
    fontSize: 13,
    lineHeight: '18px',
    letterSpacing: '-0.014em',
    color: 'rgba(var(--text-label-rgb), 0.55)',
    textAlign: 'center',
  };

  if (confirming && !scheduled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 24 }}>
        <p style={{ ...text, maxWidth: 520, color: INK }}>{t('settings.plan.cancelConfirm')}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="outlined" size="small" onClick={click('cancel_plan_dismiss', () => setConfirming(false))} disabled={canceling}>
            {t('settings.plan.keepPlan')}
          </Button>
          <Button color="destructive" size="small" onClick={click('cancel_plan_confirm', onCancel)} disabled={canceling} loading={canceling}>
            {t('settings.plan.cancelPlan')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="sym-sub-line" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: 12, rowGap: 4, marginTop: 24 }}>
      <p style={text}>
        {planName}
        {' · '}
        {status}
      </p>
      {scheduled ? (
        <TextButton size="small" underline className="sym-sub-link" onClick={click('keep_plan', onKeepPlan)} loading={keeping}>
          {t('settings.plan.keepPlan')}
        </TextButton>
      ) : (
        <TextButton size="small" underline className="sym-sub-link" onClick={click('cancel_plan', () => setConfirming(true))}>
          {t('settings.plan.cancelPlan')}
        </TextButton>
      )}
    </div>
  );
}

/**
 * The blue plate a marked card wears — "Recommended" on the tier the catalog pushes, "Current plan"
 * on the buyer's own. One construction for both, because the frame draws them the same way and two
 * copies would be free to drift apart.
 *
 * It renders inside the card and positions against it, so the caller only supplies the word. At most
 * one is ever drawn per card: `showRecommendedBanner` is suppressed on the current card by design.
 */
function CardPlate({ label }: { label: string }) {
  return (
    <>
      {/* The blue plate, BEHIND the card — the frame's own construction.

          `z-index: -1` is what puts it there: a negative-z-index child paints before its
          parent's background, so the card's white covers the plate's middle and only the rise
          and the bleed show. It cannot escape the picker either — `.sym-plans-ct` carries
          `container-type`, whose layout containment makes it a stacking context, so the plate
          is trapped inside it rather than sliding behind the pane's own background.

          This is the part a box-shadow could not do: the plate has its OWN radius, tighter than
          the card's, so the outer silhouette is crisp while the card stays soft and the blue
          fills the difference at the corners. Offsets are against the PADDING box, and the
          card's 2px border sits outside that — hence the +2 on every edge. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          zIndex: -1,
          top: -(RECOMMENDED_BANNER_RISE + 2),
          insetInline: -(RECOMMENDED_PLATE_BLEED + 2),
          bottom: -(RECOMMENDED_PLATE_BLEED + 2),
          borderRadius: RECOMMENDED_PLATE_RADIUS,
          background: BADGE_BLUE,
        }}
      />
      {/* The label sits in the rise. No fill of its own — the plate behind provides it.
          Bold is the frame's weight; the SIZE is what makes it read heavy or not, and the frame's
          20px is against its 415px card — this one renders ~317, so the proportional size is 15.
          An earlier 18 was scaled off the 370 track rather than the rendered width and read
          overbearing. line-height is the frame's 1.3, and it is doing real work: at 1 the line box
          is shorter than the font's own ascent+descent, and since "Recommended" has no descenders
          the ink drifts above the strip's centre. 1.3 restores the half-leading that balances it. */}
      <div
        style={{
          position: 'absolute',
          top: -(RECOMMENDED_BANNER_RISE + 2),
          insetInline: -2,
          height: RECOMMENDED_BANNER_RISE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Descender compensation, and it is not a fudge: flex centring centres the LINE BOX,
          // which reserves space below the baseline for descenders. English "Recommended" has
          // none, so the visible glyphs end at the baseline and the whole word rides high —
          // measured at 3.6px of blue above it against 7.4 below. Padding the top pushes the
          // centred band down by half of it, which lands the CAP box in the middle. border-box so
          // the div still ends exactly at the card's edge (no global border-box reset here).
          //
          // It is a fixed 4 rather than a per-label one, and that is a deliberate limit: whether a
          // string descends is a property of the WORD, so it already varies across the 37 bundles
          // (German "Empfohlen" descends where English does not) and now across the two labels
          // too. Both plates take the same nudge because the point of one construction is that the
          // two read identically; a 2px per-plate divergence would cost more than it buys.
          boxSizing: 'border-box',
          paddingTop: 4,
          fontFamily: MADEFOR_TEXT,
          fontWeight: 'var(--sym-weight-medium)',
          fontSize: 18,
          lineHeight: 1.3,
          letterSpacing: '-0.02em',
          color: 'var(--ink-on-fill)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
    </>
  );
}

function PlanCard({
  plan,
  showTopupTrack,
  showCouponTrack,
  cadence,
  currency,
  pendingId,
  busy,
  activePlanIds,
  outranksRecommended,
  saleActive,
  isTopTier,
  hasRealSubscription,
  subCadenceByPlanId,
  switchingId,
  pendingPlanId,
  pendingDate,
  cancellationDate,
  couponsEnabled,
  cycleUnasked,
  onPickCycle,
  onCheckout,
  onTopup,
  onSwitch,
}: {
  plan: PickerPlan;
  /** The grid carries a top-up track — every card must render it (filled or empty) or the bands desync. */
  showTopupTrack: boolean;
  /** Likewise the promo-code track. */
  showCouponTrack: boolean;
  cadence: Cadence;
  /** ISO currency for the price symbol (picker-level, from GET /api/plans). */
  currency?: string;
  pendingId: string | null;
  /** True while ANY checkout or switch is in flight — disables every card's CTA. */
  busy: boolean;
  activePlanIds: Set<string>;
  /** True when the buyer's own plan is a bigger tier than the recommended one — see
   *  {@link outranksRecommendedTier}. Suppresses the whole recommended treatment. */
  outranksRecommended: boolean;
  /** Personal-sale offer live AND covering THIS card's tier — the parent has already applied
   *  {@link personalSaleAppliesToPlan}, so a Basic card receives `false` here even mid-sale. On
   *  YEARLY, frame this card (if recommended or top-tier) as "30% OFF" and show the
   *  discounted/struck price; the matching coupon is carried into checkout by `onCheckout`. */
  saleActive: boolean;
  /** True for the most expensive plan (Max) — the sale frames it alongside the recommended tier. */
  isTopTier: boolean;
  /** True only when a REAL (webhook-granted) subscription exists — gates in-place switching, which
   *  the optimistic post-purchase seed can't satisfy server-side. */
  hasRealSubscription: boolean;
  /** planId → the cadence the user is subscribed at (a plan is "current" only at that cadence). */
  subCadenceByPlanId: Map<string, Cadence>;
  /** agentPlanId currently being switched to (this card shows "Switching…"). */
  switchingId: string | null;
  /** agentPlanId of a pending deferred downgrade's target, if any (that card shows "Scheduled"). */
  pendingPlanId: string | null;
  /** Formatted date the pending downgrade takes effect. */
  pendingDate: string;
  /** Formatted date a pending cancel-at-period-end lapses, or '' when none is scheduled. */
  cancellationDate: string;
  /** Sandbox billing intentionally has no coupon rail. */
  couponsEnabled: boolean;
  /**
   * Variant arm: the cycle has NOT been asked yet, so this card is not selling one. It shows an
   * indicative per-month price, treats the buyer's plan as theirs whichever cycle they hold it at
   * (`ownsOption`), and hands the option to the step rather than committing to a cadence.
   */
  cycleUnasked: boolean;
  /**
   * Variant arm only: open the billing-cycle step for this card instead of buying straight away.
   * `undefined` in control, which is also what makes it the branch condition below — a card cannot
   * be in the variant and have nowhere to send the buyer.
   */
  onPickCycle?: (plan: PickerPlan, option: PickerOption) => void;
    onCheckout: (agentPlanId: string, cadence: Cadence, planKey: string, couponCode?: string) => void;
  /** Start checkout for this card's one-time top-up pack. */
  onTopup: (packId: string) => void;
  onSwitch: (plan: PickerPlan, option: PickerOption, cadence: Cadence) => void;
}) {
  const { t } = useTranslation();
  // The card's own logger, not one passed down: `component_route` is read from the provider chain at
  // the point the hook runs, so a logger created in the parent would report the parent's route and
  // miss the cadence segment wrapped around these cards.
  const { logPageClickBi } = useGenericBi('packages');
  const attribution = usePurchaseAttribution();
  // If one of this card's options is the user's current plan, preselect it (e.g. the Max card
  // defaults to Max+ when Max+ is the active subscription), so the current-plan marker lands on
  // the option they actually own.
  const currentOptionIdx = plan.options.findIndex((o) => activePlanIds.has(o.agentPlanId));
  // Max carries 2 options (Max / Max+); selecting flips the price + features.
  //
  // The card opens on the LARGEST tier — Max+ (30k), not Max (12k). Product decision, and it
  // deliberately differs from the frame, which draws 12,000 in the dropdown.
  //
  // Found by credits rather than taken as an index: the catalog is supposed to sort a card's options
  // ascending, so "the last one" would work today, but a default that depends on payload order flips
  // to a different price the moment that order changes, silently.
  const defaultOptionIdx = useMemo(
    () => plan.options.reduce((best, o, i, all) => (o.credits > all[best].credits ? i : best), 0),
    [plan.options],
  );
  const [optionIdx, setOptionIdx] = useState(currentOptionIdx >= 0 ? currentOptionIdx : defaultOptionIdx);
  // Premium loads AFTER first render, so the useState initializer above can't see the owned
  // option yet. Once it resolves, snap the dropdown to the subscribed option (e.g. Max+ = 30k)
  // so the card shows "Current plan" on the right tier. Only fires when currentOptionIdx
  // actually resolves, so it won't clobber a manual selection on non-current cards.
  useEffect(() => {
    if (currentOptionIdx >= 0) setOptionIdx(currentOptionIdx);
  }, [currentOptionIdx]);
  const option: PickerOption | undefined = plan.options[optionIdx];
  const featureKeys = option?.featureKeys ?? [];
  const pending = option != null && pendingId === option.agentPlanId;
  // A paid option is purchasable only when the SELECTED cadence is priced. Never let the buyer
  // check out an unpriced cadence — the server would still resolve the real Stripe price id and
  // charge for it, so a "$0"/missing price on screen must hard-disable the CTA.
  const purchasable = optionPurchasable(option, cadence, cycleUnasked);
  // The user's current plan: is the SELECTED option their active subscription AT THE CADENCE
  // currently shown? Keyed on both plan AND cadence, so the same plan's other cadence (e.g. the
  // yearly version of a monthly subscription) is NOT "current" — it's an upgrade/downgrade offer.
  const isCurrentOption = option != null && ownsOption(subCadenceByPlanId.get(option.agentPlanId), cadence, cycleUnasked);
  // If the user already has an active subscription, the other cards aren't a first purchase —
  // they're a plan change, so their CTA says so ("Switch to <plan>") rather than "Get started".
  const isSubscribed = activePlanIds.size > 0;
  // Subscribed purely from the optimistic post-purchase seed — the real webhook grant hasn't landed,
  // so an in-place SwitchPlan isn't possible yet. During this brief window the OTHER (non-current)
  // cards are disabled rather than offering a "Switch" that would error, or a second checkout.
  const optimisticPendingActivation = isSubscribed && !hasRealSubscription;
  // "Current" used to split in two, because the Free card was the current plan of everyone who had
  // not bought anything while carrying none of a bought card's chrome. With the picker selling only,
  // both questions have the same answer: this card's selected option is the buyer's subscription.
  const isCurrentStatus = isCurrentOption;
  const isCurrentCard = plan.options.some((o) => ownsOption(subCadenceByPlanId.get(o.agentPlanId), cadence, cycleUnasked));
  // This card's selected option is currently being switched to (shows "Switching…").
  const isSwitching = option != null && switchingId === option.agentPlanId;
  // This card's selected option is the target of a pending deferred downgrade (shows "Scheduled").
  const isScheduledTarget = option != null && pendingPlanId != null && option.agentPlanId === pendingPlanId;
  // Cancelling and keeping the plan live in SubscriptionLine under the cards; the card only shows
  // the result. Only on a real, webhook-granted subscription — never the optimistic post-purchase
  // window, where there's no server-side subscription to cancel yet.
  const cancellationScheduled = isCurrentCard && hasRealSubscription && cancellationDate !== '';

  // A promo code applied to THIS card. A coupon is validated against one checkout target
  // (plan + cadence + currency), so it is dropped the moment the target moves — switching the
  // Max/Max+ tier or flipping the cadence toggle invalidates the discount that was resolved.
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  useEffect(() => {
    setCoupon(null);
  }, [option?.agentPlanId, cadence]);
  // Coupons ride on CreateCheckoutSession only — SwitchPlan has no coupon entry point (ADR-029),
  // so an existing subscriber's "Switch to…" card offers no code field.
  const showCoupon = couponsEnabled && purchasable && !isCurrentStatus && !isSubscribed && !isScheduledTarget;

  // The recommended tier wears a blue plate rising RECOMMENDED_BANNER_RISE out of the card's top
  // edge, plus a blue stroke — not the corner badge it used to.
  // Suppressed on the buyer's own current card, which already carries the accent outline + badge,
  // and on every card once the buyer's plan outranks this tier (a Max/Max+ subscriber must not be
  // pointed DOWN at Pro) — see `outranksRecommendedTier`.
  //
  // Personal sale (yearly only): the offer frames BOTH the recommended tier AND the top tier (Max),
  // and both wear the "30% OFF" label instead of "Recommended". On monthly — the cadence the offer
  // does not discount — the plate reverts to the plain recommended treatment on Pro alone.
  //
  // Once the buyer applies their OWN coupon, the personal-sale visual steps aside (`!coupon`): checkout
  // uses their code, not `PERSONAL_SALE_COUPON` (see `buyPlan`), so the card must show their coupon's
  // own saving via `CouponField`, not the 30%-off headline — display must match the charge.
  const saleForCard = saleActive && !coupon;
  const saleYearly = saleForCard && cadence === 'yearly';
  const showRecommendedBanner =
    !isCurrentCard && !outranksRecommended && (plan.recommended === true || (saleYearly && isTopTier));
  const plateLabel = saleYearly ? t('personalSale.badge') : t('pricing.recommended');

  return (
    <article
      className={`sym-card${
        showRecommendedBanner ? ' sym-card-recommended' : plan.recommended === true ? ' sym-card-pushed' : ''
      }`}
      style={{
        position: 'relative',
        padding: '36px 30px',
        // Flat: the white card on the grey field carries the separation, so no drop shadow at all.
        // The stroke is an indicator, never elevation: only the recommended tier wears it.
        background: CARD_BG,
        borderRadius: 20,
        // Every card reserves the same border width, so adding the recommended stroke costs no
        // reflow and the plate's offsets stay measured against the same border box.
        border: showRecommendedBanner ? `2px solid ${BADGE_BLUE}` : '2px solid transparent',
        // The frame gives EVERY card a 1px hairline; without it a white card on the grey field has
        // no edge at all, which is what made the picker read flat. An OUTLINE rather than a border so
        // the recommended card's 2px blue stays the only thing in the box model — a 1px border here and
        // 2px there would leave the two card heights a pixel apart in a shared subgrid row.
        outline: showRecommendedBanner ? 'none' : '1px solid var(--ds-border-light-default)',
        outlineOffset: -1,
      }}
    >
      {showRecommendedBanner && <CardPlate label={plateLabel} />}

      {/* Name + tagline (subgrid row 1). minHeight is a floor for the stacked/2-up fallback;
          in the one-row subgrid layout the shared row track aligns titles regardless of how
          many lines each tagline wraps to. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 86 }}>
        <div style={{ fontFamily: ARCHIVO, fontWeight: 'var(--sym-weight-semibold)', fontSize: 32, lineHeight: 1.06, letterSpacing: '-0.02em', color: INK }}>
          {t(plan.nameKey)}
        </div>
        <p style={{ margin: 0, fontFamily: MADEFOR_TEXT, fontSize: 18, lineHeight: '22px', letterSpacing: '-0.02em', color: INK }}>
          {t(plan.taglineKey)}
        </p>
      </div>

      {/* Price + credits (subgrid row 2). The shared subgrid row track auto-sizes to the tallest
          card's price block (Max's dropdown + note) and keeps prices — and the CTA below —
          aligned across cards, so no minHeight reservation is needed. */}
      {/* The frame also draws a "Price includes VAT" line here. It is NOT rendered, and the reason is
          worth keeping: the only predicate we have, `showsTaxInclusivePrices`, means "the shown price
          is the charged price" — deliberately a statement of SILENCE about tax (see its doc). It is
          true for markets where nothing is collected at all (Brazil, approved), for the four markets
          sitting unregistered until their filing lands, and for GST/consumption-tax markets that do
          not call it VAT. Turning it into an affirmative "includes VAT" asserts something false in
          every one of those. A real VAT note needs a per-market answer no code here can derive — the
          FOURTH per-market decision that file already warns about. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <PriceRow
          option={option}
          cadence={cadence}
          currency={currency}
          cycleUnasked={cycleUnasked}
          saleActive={saleForCard}
          ownedCadence={option ? subCadenceByPlanId.get(option.agentPlanId) : undefined}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {plan.options.length > 1 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              <CreditsSelect name={plan.key} options={plan.options} selectedIdx={optionIdx} onSelect={setOptionIdx} />
              {option?.note && <div style={CREDITS_LINE_STYLE}>{option.note}</div>}
            </div>
          ) : (
            <div style={CREDITS_LINE_STYLE}>
              {option != null && t('pricing.monthlyCredits', { credits: option.credits.toLocaleString() })}
            </div>
          )}
        </div>
      </div>

      {/* CTA (subgrid row 3) — buttons align across the row. */}
      {isCurrentStatus ? (
        // The user's current plan — not a CTA, a non-clickable status.
        <div
          aria-label={t('pricing.currentPlanAria')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            boxSizing: 'border-box',
            padding: '12px 15px',
            borderRadius: 'var(--ds-radius-10)',
            // The accent marks a plan you BOUGHT, which every card here now is.
            background: accentA(0.22),
            color: INK,
            fontFamily: ARCHIVO,
            fontWeight: 'var(--sym-weight-semibold)',
            fontSize: 16,
            lineHeight: 1.3,
            letterSpacing: '-0.03em',
          }}
        >
          {cancellationScheduled ? (
            t('settings.plan.cancelsOn', { date: cancellationDate })
          ) : (
            <>
              <CheckIcon stroke={INK} />
              {t('pricing.currentPlan')}
            </>
          )}
        </div>
      ) : isScheduledTarget ? (
        // A deferred downgrade is already scheduled to this option — not a CTA, a muted status
        // (undo lives in the banner at the top).
        <div
          aria-label={t('pricing.scheduledPlanChangeAria')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            boxSizing: 'border-box',
            padding: '12px 15px',
            borderRadius: 'var(--ds-radius-10)',
            background: 'var(--sym-bg-neutral-medium)',
            color: 'rgba(var(--ink-rgb), 0.75)',
            fontFamily: ARCHIVO,
            fontWeight: 'var(--sym-weight-semibold)',
            fontSize: 15,
            lineHeight: 1.3,
            letterSpacing: '-0.02em',
          }}
        >
          {pendingDate ? t('pricing.scheduledWithDate', { date: pendingDate }) : t('pricing.scheduled')}
        </div>
      ) : (
        <button
          type="button"
          className="sym-cta"
          disabled={busy || !purchasable || optimisticPendingActivation}
          onClick={() => {
            if (!purchasable || !option) return;
            // A just-bought plan whose grant is still pending: no valid action yet (switch would have
            // no real sub; checkout would double-buy). The card is disabled — this is a safety no-op.
            if (optimisticPendingActivation) return;
            // Variant arm: the cycle has not been asked yet, so this card buys nothing on its own —
            // it hands the chosen plan to the billing-cycle step, which performs whichever of the two
            // actions below applies once it knows the cadence. Subscribers included: this arm removes
            // the cadence toggle for everyone, so routing them past the step would leave them unable
            // to choose a cycle at all.
            if (onPickCycle) {
              // The variant's ONLY plan-choice signal. Here the card buys nothing — it hands the plan
              // to the billing-cycle step — so without this the arm had no countable "picked a plan"
              // step and its funnel started at the step's own impression.
              //
              // Deliberately NOT fired on the control branch below: there this same click IS the
              // purchase and already reports `buy_plan`, and adding a second event to it would change
              // what the existing funnel counts.
              logPageClickBi(
                { page_name: 'packages', button_name: 'select_plan', action: 'select' },
                buyPlanBi(plan, option, cadence),
              );
              return onPickCycle(plan, option);
            }
            // Existing subscribers change plans in place (SwitchPlan, card on file); a first
            // purchase goes through Stripe checkout.
            if (isSubscribed) return onSwitch(plan, option, cadence);
            // Fired only on the checkout branch, which is where it fired from `buyPlan` before it
            // moved here — a switch has never been a `buy_plan`, and making it one now would change
            // what the existing funnel counts.
            logPageClickBi({ page_name: 'packages', button_name: 'buy_plan' }, buyPlanBi(plan, option, cadence, attribution));
            return onCheckout(option.agentPlanId, cadence, plan.key, coupon?.code);
          }}
          style={{
            appearance: 'none',
            // No `border` here: it is what separates the outlined resting state from the two filled
            // ones, and an inline one would beat `.sym-cta` and flatten all three. `border-box` is
            // the UA default for a button, but this is now load-bearing — a 2px border on a
            // width: 100% element — so it is stated rather than inherited from a stylesheet we
            // cannot see from here.
            width: '100%',
            boxSizing: 'border-box',
            minHeight: 44,
            padding: '12px 16px',
            borderRadius: 8,
            fontFamily: MADEFOR_TEXT,
            fontWeight: 'var(--sym-weight-semibold)',
            fontSize: 18,
            lineHeight: '20px',
            letterSpacing: '-0.02em',
            cursor: busy || !purchasable || optimisticPendingActivation ? 'not-allowed' : 'pointer',
            opacity: (busy || !purchasable || optimisticPendingActivation) && !pending && !isSwitching ? 0.5 : 1,
          }}
        >
          {isSwitching
            ? t('pricing.cta.switching')
            : pending
              ? t('pricing.cta.redirecting')
              : !purchasable
                ? cadence === 'yearly'
                  ? t('pricing.cta.notAvailableYearly')
                  : t('pricing.cta.notAvailableMonthly')
                : isSubscribed
                  ? t('pricing.cta.switchTo', { planName: t(plan.nameKey) })
                  : t('pricing.cta.getStarted')}
        </button>
      )}

      {/* Top-up (subgrid row 4) — a one-time credit pack, offered ONLY on the buyer's current plan
          OPTION (a top-up adds credits to the subscription you're on; the picker never sells one
          alongside a plan you don't hold). Keyed to the option, not the card: Max and Max+ share a
          card but only Max+ carries a pack, so a Max subscriber sees nothing. Renders only when the
          option carries a priced pack (option.topup — the server omits it entirely when the
          pack/price didn't resolve, so this can never render without a real amount). Every other
          card renders the empty placeholder so the shared subgrid row keeps the what's-included
          band aligned — same pattern as the promo-code row. Gated on hasRealSubscription (not the
          optimistic post-purchase seed): during that brief window the plan's own grant hasn't
          landed, so we don't invite a second charge yet. */}
      {isCurrentStatus && hasRealSubscription && option?.topup ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: '14px 16px 16px',
            borderRadius: 'var(--sym-radius-container)',
            // The frame's pale blue, and it belongs to the same family as the card's stroke and its
            // plate — the panel only ever appears on the current-plan card, so the blue reads as
            // part of that card's marking rather than as a colour of its own.
            // An ALPHA of the blue rather than a ramp step (`--ds-blue-50`), for the reason the
            // progress track's own tint documents: a ramp step is mode-independent, so it would stay
            // a pale blue wash on a near-black card in dark mode. 0.08 over white composites to
            // #EFF3FD against the frame's #EDF2FD, and it is the alpha the sibling blue tint in this
            // file already uses.
            background: 'rgba(var(--ds-text-in-progress-rgb), 0.08)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontFamily: MADEFOR_DISPLAY, fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: INK }}>
              {t('pricing.topup.title')}
            </div>
            {/* The one line that explains the product: extra credits on top of the plan, no expiry,
                no recurring charge. Everything else on the panel is amounts. */}
            <p style={{ margin: 0, fontFamily: MADEFOR_TEXT, fontSize: 13, lineHeight: 1.35, color: BODY_INK }}>
              {t('pricing.topup.explainer')}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CoinsIcon />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontFamily: MADEFOR_DISPLAY, fontWeight: 700, fontSize: 17, letterSpacing: '-0.01em', color: INK }}>
                {t('pricing.topup.credits', { credits: option.topup.credits.toLocaleString() })}
              </div>
              <div style={{ fontFamily: MADEFOR_TEXT, fontSize: 13, color: BODY_INK }}>
                {t('pricing.topup.oneTime', { price: formatMoney(option.topup.amount, option.topup.currency ?? currency) })}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="sym-topup"
            disabled={busy}
            onClick={() => option.topup && onTopup(option.topup.packId)}
            style={{
              appearance: 'none',
              border: 'none',
              width: '100%',
              padding: '10px 16px',
              borderRadius: 'var(--sym-radius-button)',
              fontFamily: ARCHIVO,
              fontWeight: 'var(--sym-weight-medium)',
              fontSize: 15,
              letterSpacing: '-0.03em',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy && pendingId !== option.topup.packId ? 0.5 : 1,
            }}
          >
            {pendingId === option.topup.packId ? t('pricing.cta.redirecting') : t('pricing.topup.cta')}
          </button>
        </div>
      ) : showTopupTrack ? (
        <div aria-hidden="true" />
      ) : null}

      {/* What's included — the "What you get?" header aligns across the row. The ticks are blue on
          the recommended card and ink on every plain card, including the buyer's current plan. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ fontFamily: MADEFOR_TEXT, fontWeight: 'var(--sym-weight-medium)', fontSize: 18, letterSpacing: '-0.02em', color: BODY_INK }}>
          {t('pricing.whatYouGet')}
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {featureKeys.map((featureKey) => {
            const infoKey = featureInfoKey(featureKey);
            return (
              <li key={featureKey} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckIcon width={14} height={12} stroke={showRecommendedBanner ? BADGE_BLUE : undefined} />
                <span style={{ fontFamily: MADEFOR_TEXT, fontSize: 16, lineHeight: '22px', color: 'var(--ds-text-primary)' }}>{t(featureKey)}</span>
                {infoKey && <FeatureInfo text={t(infoKey)} />}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Promo code — the LAST band on the card, under the feature list. Once the grid carries the
          track, EVERY card renders it (a card that offers no field renders it empty) so the cards'
          bottom edges stay level whether or not it's expanded. */}
      {showCoupon && option ? (
        <CouponField
          planName={t(plan.nameKey)}
          planKey={plan.key}
          option={option}
          cadence={cadence}
          currency={currency}
          applied={coupon}
          onApply={setCoupon}
          onRemove={() => setCoupon(null)}
          disabled={busy}
        />
      ) : showCouponTrack ? (
        <div aria-hidden="true" />
      ) : null}
    </article>
  );
}

// The credit allotment under the price — the frame's Display 20/26 Bold, scaled to this pane.
const CREDITS_LINE_STYLE: CSSProperties = {
  fontFamily: MADEFOR_TEXT,
  fontWeight: 'var(--sym-weight-semibold)',
  fontSize: 18,
  lineHeight: '22px',
  letterSpacing: '-0.02em',
  color: BODY_INK,
};


function PriceRow({
  option,
  cadence,
  currency,
  cycleUnasked,
  saleActive,
  ownedCadence,
}: {
  option?: PickerOption;
  cadence: Cadence;
  currency?: string;
  /** Variant arm: show the per-month figure, but claim NO billing cycle — see below. */
  cycleUnasked: boolean;
  /** Personal-sale offer live: on YEARLY, show the 30%-off discounted headline with the original
   *  struck through. Display-only — no coupon is charged yet (see lib/personal-sale). */
  saleActive: boolean;
  /** The cadence the buyer holds THIS option at, if they hold it. Drives `cardCadence`. */
  ownedCadence?: Cadence;
}) {
  const { t } = useTranslation();

  // The cadence this CARD prices itself at — normally the one on screen, but see `cardCadence`: with
  // the cycle unasked it follows what the buyer owns (so a card marked "Current plan" never quotes a
  // price they do not pay) and otherwise whatever the option is actually priced at.
  const shown = cardCadence(option, cadence, cycleUnasked, ownedCadence);
  // Unpriced for that cadence — show it plainly, never "$0" (the CTA is disabled too).
  const cadenceAmount = shown === 'yearly' ? option?.yearlyAmount : option?.monthlyAmount;
  if (!option || cadenceAmount == null) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', minHeight: 47 }}>
        <span style={{ fontFamily: MADEFOR_DISPLAY, fontWeight: 'var(--sym-weight-semibold)', fontSize: 16, color: 'rgba(var(--text-label-rgb), 0.55)' }}>
          {shown === 'yearly' ? t('pricing.cta.notAvailableYearly') : t('pricing.cta.notAvailableMonthly')}
        </span>
      </div>
    );
  }

  // Yearly: show the per-month equivalent (headline). The "Billed <total> / year" note that used to
  // sit under it has been removed for every card — the per-month headline is the number the frame
  // draws and the one that's comparable across cards.
  const headline = shown === 'yearly' ? Math.round(cadenceAmount / 12) : Math.round(cadenceAmount);
  // Personal sale (yearly only): the offer discounts the yearly per-month price by 30%. Show the
  // discounted number as the headline with the original struck through beside it (Figma
  // 16946-168860). Display-only — the checkout charge is NOT yet discounted (see lib/personal-sale).
  const saleYearly = saleActive && shown === 'yearly';
  const shownHeadline = saleYearly ? personalSaleDiscounted(headline) : headline;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <PriceHeadline
        text={formatMoney(shownHeadline, currency)}
        suffix={t('pricing.perMonth')}
        strikethrough={saleYearly ? formatMoney(headline, currency) : undefined}
      />
    </div>
  );
}

// The design's headline size, and the floor it may shrink to. 52 is what a normal price renders at
// and must keep rendering at — the fit below only ever takes size AWAY, never adds it.
const PRICE_SIZE_PX = 64;
const PRICE_MIN_SIZE_PX = 22;
const PRICE_SUFFIX_GAP = 5;

const PRICE_NUMBER_STYLE: CSSProperties = {
  fontFamily: ARCHIVO,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: 0.83,
  letterSpacing: '-0.05em',
  color: INK,
  whiteSpace: 'nowrap',
};
const PRICE_SUFFIX_STYLE: CSSProperties = {
  fontFamily: SERIF,
  fontSize: 24,
  letterSpacing: '-0.02em',
  color: INK,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
// The personal-sale ORIGINAL price, struck through and muted, sitting before the discounted headline
// ("$40̶  $32 / month", Figma 16946-168860). A FIXED size (not tied to the fitted headline size) so it
// cannot feed back into the fit calc — its width is subtracted from the headline's available space.
const PRICE_STRIKE_SIZE_PX = 30;
const PRICE_STRIKE_GAP = 8;
const PRICE_STRIKE_STYLE: CSSProperties = {
  fontFamily: ARCHIVO,
  fontWeight: 'var(--sym-weight-semibold)',
  fontSize: PRICE_STRIKE_SIZE_PX,
  lineHeight: 0.83,
  letterSpacing: '-0.05em',
  color: 'rgba(var(--text-label-rgb), 0.55)',
  textDecoration: 'line-through',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/**
 * The price headline — `text` already formatted by `formatMoney`, `suffix` the "/ month" run beside
 * it — shrunk to fit its card, and only when it doesn't.
 *
 * A fixed 52px is right for `$100` and wrong for the markets whose formatted price is simply longer:
 * `CHF 190` ran out of room and wrapped the suffix onto a second line, and Vietnam's `6,000,000 ₫`
 * overran the card entirely. Both are the same defect — the headline's width is a property of the
 * CURRENCY, and no fixed size can be correct for all of them.
 *
 * It cannot be done in CSS. `min(52px, N cqi)` scales with the container, which is exactly what we
 * do NOT want (it would shrink `$100` on a narrow pane while leaving `6,000,000 ₫` overflowing on a
 * wide one) — the input has to be the rendered width of THIS string. So it is measured, once per
 * string, off a hidden twin held at the full size: the twin's width never changes as the visible
 * span shrinks, so there is no measure→resize→measure loop. Width scales linearly with font-size in
 * a given face, so the fitted size is one division rather than a search.
 *
 * `$100` measures well inside its space and stays at 52 — the guard test pins that.
 */
function PriceHeadline({ text, suffix, strikethrough }: { text: string; suffix: string; strikethrough?: string }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const twinRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);
  const strikeRef = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState(PRICE_SIZE_PX);

  useEffect(() => {
    const fit = () => {
      const row = rowRef.current;
      const twin = twinRef.current;
      if (!row || !twin) return;
      const natural = twin.getBoundingClientRect().width;
      // The struck original (personal sale) sits before the number at a FIXED size, so its width is a
      // fixed claim on the row like the suffix — subtract it (plus its gap) rather than letting it feed
      // back into the fitted size.
      const strikeWidth = strikeRef.current
        ? strikeRef.current.getBoundingClientRect().width + PRICE_STRIKE_GAP
        : 0;
      const available =
        row.clientWidth - (suffixRef.current?.getBoundingClientRect().width ?? 0) - PRICE_SUFFIX_GAP - strikeWidth;
      // A row that hasn't been laid out yet (0 width) must not be treated as "no room".
      if (natural <= 0 || available <= 0) return;
      const fitted = Math.floor((PRICE_SIZE_PX * available) / natural);
      setSize(Math.max(PRICE_MIN_SIZE_PX, Math.min(PRICE_SIZE_PX, fitted)));
    };
    fit();
    // The track width changes with the pane (and with the chat docking beside it), so re-fit on
    // resize; and re-fit once webfonts land, since the twin measured in a fallback face is wrong.
    const observer = new ResizeObserver(fit);
    if (rowRef.current) observer.observe(rowRef.current);
    let cancelled = false;
    document.fonts?.ready.then(() => !cancelled && fit()).catch(() => {});
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [text, suffix, strikethrough]);

  return (
    <div
      ref={rowRef}
      style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: PRICE_SUFFIX_GAP, minWidth: 0 }}
    >
      {strikethrough != null && (
        <span
          ref={strikeRef}
          // Decorative: the DISCOUNTED number beside it is the real price a screen reader should read;
          // announcing the struck original too would read two prices. The "30% OFF" plate carries the
          // discount for AT.
          aria-hidden="true"
          style={{ ...PRICE_STRIKE_STYLE, marginInlineEnd: PRICE_STRIKE_GAP - PRICE_SUFFIX_GAP }}
        >
          {strikethrough}
        </span>
      )}
      <span style={{ ...PRICE_NUMBER_STYLE, fontSize: size }}>{text}</span>
      <span ref={suffixRef} style={PRICE_SUFFIX_STYLE}>
        {suffix}
      </span>
      {/* The measuring twin: full size, out of flow, hidden from painting and from AT. Absolute
          rather than `display: none` because a display-none element has no box to measure.

          It lives inside a 0x0 CLIPPING wrapper, and that is load-bearing. At full size the twin is
          by definition wider than the space the visible price had to shrink into, so it overflows on
          the INLINE axis — and inline overflow is scrollable overflow, which propagates up to the
          content pane, whose cross axis is `auto`. That is a horizontal scrollbar across the app on
          any pane narrow enough to remove the grid's side slack. `overflow: hidden` on the wrapper
          keeps it contained; clipping does not affect the twin's laid-out width, so it still
          measures correctly. */}
      <span aria-hidden="true" style={{ position: 'absolute', inset: 0, width: 0, height: 0, overflow: 'hidden' }}>
        <span
          ref={twinRef}
          style={{
            ...PRICE_NUMBER_STYLE,
            fontSize: PRICE_SIZE_PX,
            position: 'absolute',
            insetInlineStart: 0,
            top: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>
      </span>
    </div>
  );
}

// Credit-tier picker for a multi-option card (Max / Max+): the design's bordered field with a
// trailing chevron. A native <select> rather than a hand-rolled listbox — the DS has no select
// primitive, and the platform control brings keyboard, screen-reader and touch behaviour for free.
// `name` only labels it; the selection is the parent's state, so two cards can never share one.
function CreditsSelect({
  name,
  options,
  selectedIdx,
  onSelect,
}: {
  name: string;
  options: PickerOption[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
}) {
  const { t } = useTranslation();
  // The control that decides Max vs Max+ reported NOTHING — the one place on the page where the buyer
  // picks how much they spend was invisible in BI, so "opened the tiers and stayed put" and "never
  // looked at them" were the same non-event.
  const { logPageClickBi } = useGenericBi('packages');
  // A native <select> has no "opened" event: the popup belongs to the platform. `focus` is not a
  // substitute — it also fires when a keyboard user merely tabs past — so the open is reported off
  // pointer-down on the control and the keys that actually open the popup.
  const logOpen = () =>
    logPageClickBi(
      { page_name: 'packages', button_name: 'credits_dropdown_open', action: 'open' },
      { componentName: 'credits dropdown', additionalFields: { plan_name: name } },
    );
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <select
        id={`credits-${name}`}
        aria-label={t('pricing.chooseCredits')}
        value={selectedIdx}
        onMouseDown={logOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') logOpen();
        }}
        onChange={(e) => {
          const idx = Number(e.target.value);
          const picked = options[idx];
          // A native select fires no change for re-picking the tier you are already on, so every one
          // of these is a real move between tiers.
          logPageClickBi(
            { page_name: 'packages', button_name: 'select_credits', action: 'select' },
            {
              componentName: 'credits dropdown',
              additionalFields: {
                plan_id: picked?.agentPlanId,
                plan_name: name,
                credits: picked ? String(picked.credits) : undefined,
              },
            },
          );
          onSelect(idx);
        }}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          width: '100%',
          boxSizing: 'border-box',
          paddingBlock: 11,
          paddingInlineStart: 12,
          // Clears the chevron, which sits 10 from the end and is 16 wide.
          paddingInlineEnd: 30,
          border: '0.5px solid var(--border-hover)',
          borderRadius: 'var(--ds-radius-4)',
          // The card's own white, spelled out rather than left transparent: several browsers paint
          // the dropdown's option rows from the select's computed background, and a transparent one
          // gives an unreadable popup.
          background: CARD_BG,
          fontFamily: MADEFOR_DISPLAY,
          fontWeight: 'var(--sym-weight-semibold)',
          // Matches CREDITS_LINE_STYLE, so the Max card's control and the plain credit lines on its
          // sibling cards read at one size. A native select TRUNCATES rather than wrapping or
          // ellipsising, so this has to leave room for the widest label ("30,000 monthly credits")
          // inside the track minus the chevron gutter — verify in Chrome if you change either.
          fontSize: 15,
          lineHeight: '20px',
          letterSpacing: '-0.02em',
          color: INK,
          cursor: 'pointer',
        }}
      >
        {/* Listed cheapest-first whatever order the payload arrives in. `value` stays the option's
            index in the ORIGINAL array, so sorting the display cannot desync the selection. */}
        {options
          .map((opt, idx) => ({ opt, idx }))
          .sort((a, b) => a.opt.credits - b.opt.credits)
          .map(({ opt, idx }) => (
            <option key={opt.agentPlanId} value={idx}>
              {t('pricing.monthlyCredits', { credits: opt.credits.toLocaleString() })}
            </option>
          ))}
      </select>
      <ChevronDown
        size={16}
        aria-hidden
        style={{
          position: 'absolute',
          insetInlineEnd: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: INK,
        }}
      />
    </div>
  );
}

// Coin stack for the top-up panel — same stroke language as CheckIcon (1.6px ink lines, no fill).
function CoinsIcon({ stroke = 'var(--ds-text-primary)' }: { stroke?: string }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <ellipse cx="12" cy="6" rx="7" ry="3" stroke={stroke} strokeWidth="1.6" />
      <path d="M5 6v6c0 1.66 3.13 3 7 3s7-1.34 7-3V6" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M5 12v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// The feature list draws it at the design's 12×10; the current-plan status pill keeps the 16×14
// default, where it sits beside 20px type.
function CheckIcon({
  stroke = 'var(--ds-text-primary)',
  width = 16,
  height = 14,
}: {
  stroke?: string;
  width?: number;
  height?: number;
}) {
  return (
    <svg width={width} height={height} viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path d="M1.5 7.5L5.5 11.5L14.5 1.5" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The outcome of POST /api/premium/reset, surfaced in the debug panel. Every field is optional
// because the route reports only what it actually did: a fresh account with nothing to undo answers
// `{ok:true, reason:'no_orion_presence'}` and nothing else. (`refunded` was typed as required and
// rendered unconditionally, which is why every successful reset read "Refunded undefined purchase(s)"
// — the route has never returned that field.)
export interface ResetSummary {
  reason?: string;
  refunded?: number;
  subscriptionCanceled?: boolean;
  revokedCredits?: number;
}

// What GET /api/premium/picker-debug tells the panel — server-side config the browser can't see.
interface PickerDebugInfo {
  countryCode: string;
  previewCountry: string;
  ownCountry: string;
  edgeCountry: string;
  geo: string;
  currency: string;
  allowedForCountry: boolean;
  allowedCountries: string[];
  deniedCountries: string[];
  heldCountries: string[];
  pricedGeos: string[];
  stripeMode: 'test' | 'live' | 'unknown';
  billingLane: 'live' | 'symphony-sandbox';
  billingNamespace: string;
  expectedStripeAccountId: string;
  scaConfigured: boolean;
  error?: string;
}

// Debug panel for the picker: this user's premium/credits state, the raw API payloads, and the
// Country preview for the BO premiums write allowlist — behind a subtle corner dot. Own data only.
function DebugPanel({
  premium,
  premiumError,
  plans,
  currency,
  activePlanIds,
  countryPreview,
  onCountryPreview,
  onReset,
  onClose,
}: {
  premium: PickerPremium | null;
  premiumError: string | null;
  plans: PickerPlan[] | null;
  /** The picker's resolved ISO currency (from /api/plans) — so the panel quotes the geo's own symbol. */
  currency: string | undefined;
  activePlanIds: Set<string>;
  countryPreview: string;
  onCountryPreview: (code: string) => void;
  onReset: () => Promise<ResetSummary>;
  onClose: () => void;
}) {
  // The panel's own chrome is deliberately English (a staff-only diagnostic), but the plan NAME it
  // echoes is catalog copy — it goes through `t` so the panel names the plan the card names.
  const { t } = useTranslation();
  const row: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0' };
  const mono = '"SFMono-Regular", ui-monospace, Menlo, Consolas, monospace';
  // Reset affordance state: idle → confirming → running → done/error.
  const [resetState, setResetState] = useState<'idle' | 'confirming' | 'running' | 'done' | 'error'>('idle');
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const runReset = useCallback(async () => {
    setResetState('running');
    setResetMsg(null);
    try {
      const summary = await onReset();
      setResetState('done');
      // Report only what the route says it did, so "nothing to undo" reads as that rather than as a
      // refund that never happened.
      const did = [
        summary.refunded ? `refunded ${summary.refunded} purchase(s)` : '',
        summary.subscriptionCanceled ? 'canceled subscription' : '',
        summary.revokedCredits ? `revoked ${summary.revokedCredits.toLocaleString()} credits` : '',
      ].filter(Boolean);
      setResetMsg(
        did.length
          ? `Done — ${did.join(', ')}. You are now a fresh user.`
          : `Nothing to undo${summary.reason ? ` (${summary.reason})` : ''} — already a fresh user.`,
      );
    } catch (err) {
      setResetState('error');
      setResetMsg(err instanceof Error ? err.message : 'Reset failed.');
    }
  }, [onReset]);

  // Cohort override (testing): force this account's credits mode, bypassing the created-date
  // cutoff. Honored server-side ONLY for Wix staff; a no-op otherwise (the message reflects that).
  // Mode strings mirror the CreditsMode enum.
  const [cohortMode, setCohortMode] = useState<string | null>(null);
  const [cohortMsg, setCohortMsg] = useState<string | null>(null);
  const [cohortBusy, setCohortBusy] = useState(false);
  // The raw BFF GetCreditsOverview response (mode + aggregated balance + per-source breakdown),
  // shown in the debug panel and reused for the cohort selector's current-mode readout.
  const [overviewJson, setOverviewJson] = useState<unknown>(null);
  const loadOverview = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/credits/overview`, { credentials: 'include' });
      const json = await res.json();
      setOverviewJson(json);
      if (res.ok) setCohortMode((json as { mode?: string }).mode ?? null);
    } catch (err) {
      setOverviewJson({ error: err instanceof Error ? err.message : String(err) });
    }
  }, []);
  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);
  const applyCohort = useCallback(
    async (mode: string) => {
      setCohortBusy(true);
      setCohortMsg(null);
      try {
        const res = await fetch(`${API_BASE}/api/credits/mode-override`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || json.error || 'Failed to set cohort.');
        setCohortMode(json.effectiveMode ?? null);
        setCohortMsg(
          json.overrideActive
            ? `Cohort forced to ${json.effectiveMode}.`
            : 'Not applied — this only works for Wix staff.',
        );
        // Refresh the raw overview so the balance/mode dump reflects the new cohort.
        void loadOverview();
      } catch (err) {
        setCohortMsg(err instanceof Error ? err.message : 'Failed to set cohort.');
      } finally {
        setCohortBusy(false);
      }
    },
    [loadOverview],
  );
  // Server-side geo/config state. 403 for anyone off the BO write allowlist — including a Wix
  // employee — in which case the country preview is hidden; the server would ignore it anyway.
  const [info, setInfo] = useState<PickerDebugInfo | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(withCountryPreview(`${API_BASE}/api/premium/picker-debug`, countryPreview), {
          credentials: 'include',
        });
        const json = (await res.json()) as PickerDebugInfo;
        if (!cancelled) setInfo(res.ok ? json : null);
      } catch {
        if (!cancelled) setInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countryPreview]);

  // Countries to offer: every market we price, plus every SHUT one. Since the gate became a deny list
  // there is no finite "opened" set to enumerate — an unlisted country is open and simply prices in
  // USD — so the interesting cases are the priced markets and the two kinds of closure.
  const previewCountries = useMemo(() => {
    if (!info) return [];
    const codes = new Set([...info.pricedGeos, ...info.deniedCountries, ...info.heldCountries]);
    return [...codes].sort();
  }, [info]);

  const cohortBtn = (active: boolean): CSSProperties => ({
    border: active ? '1px solid var(--ds-green-success-600)' : '1px solid var(--ds-separator-default)',
    borderRadius: 8,
    padding: '6px 10px',
    background: active ? 'var(--ds-green-success-50)' : 'var(--ds-surface-sunken)',
    color: active ? 'var(--ds-green-success-600)' : 'var(--ds-surface-sunken-dark)',
    cursor: cohortBusy ? 'default' : 'pointer',
    opacity: cohortBusy ? 0.6 : 1,
    fontFamily: ARCHIVO,
    fontSize: 12,
  });
  return (
    <div
      role="dialog"
      aria-label="Picker debug info"
      onClick={onClose}
      className="orion-pane-modal"
      style={{
        // Above the corner debug dot (z 1300) so the open panel covers it; both are portaled to
        // <body>, so this competes in the root stacking context (over the FloatingChat composer).
        zIndex: 1400,
        background: 'rgba(var(--ds-black-rgb), 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <ScrollArea
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          maxHeight: '82vh',
          background: 'var(--ds-surface-sunken)',
          borderRadius: 14,
          padding: 22,
          fontFamily: mono,
          fontSize: 12.5,
          color: 'var(--ds-surface-sunken-dark)',
          boxShadow: '0 20px 60px rgba(var(--ds-black-rgb), 0.35)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <strong style={{ fontFamily: ARCHIVO, fontSize: 16 }}>Picker debug</strong>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {info && (
          <DebugSection title="Country (preview)">
            <div style={{ color: 'var(--ds-text-secondary)', marginBottom: 8 }}>
              Re-price the picker as a buyer in another country. Changes only what YOU see — checkout
              still charges your real geo, so this previews the catalog, not a purchase.
            </div>
            <select
              value={countryPreview}
              onChange={(e) => onCountryPreview(e.target.value)}
              style={{
                width: '100%',
                border: '1px solid var(--ds-separator-default)',
                borderRadius: 8,
                padding: '7px 9px',
                background: 'var(--ds-surface-sunken)',
                color: 'var(--ds-surface-sunken-dark)',
                fontFamily: ARCHIVO,
                fontSize: 12,
              }}
              aria-label="Preview the picker as a buyer in this country"
            >
              <option value="">Off — my own country ({info.ownCountry || info.edgeCountry || 'unknown'})</option>
              {previewCountries.map((code) => (
                <option key={code} value={code}>
                  {countryLabel(code)}
                  {info.deniedCountries.includes(code)
                    ? ' — sanctioned'
                    : info.heldCountries.includes(code)
                      ? ' — held'
                      : ''}
                  {info.pricedGeos.includes(code) ? '' : ' · unpriced'}
                </option>
              ))}
            </select>
            <div style={{ marginTop: 8 }}>
              <div style={row}><span>country</span><span>{info.countryCode || '—'}</span></div>
              <div style={row}><span>price geo</span><span>{info.geo || '—'}</span></div>
              {/* Both rows from the SAME picker-debug response — mixing in the picker's own /api/plans
                  state here once paired a fresh "geo CA" with the previous preview's stale "EUR". */}
              <div style={row}><span>currency</span><span>{info.currency.toUpperCase()}</span></div>
              {currency != null && currency.toLowerCase() !== info.currency.toLowerCase() && (
                <div style={{ marginTop: 4, color: 'var(--ds-orange-800)' }}>
                  The loaded catalog's Stripe prices came back in {currency.toUpperCase()}, not{' '}
                  {info.currency.toUpperCase()} — the catalog quotes this geo a different currency than
                  configured. That is a catalog finding, not a display bug.
                </div>
              )}
              <div style={row}>
                <span>region gate</span>
                <span style={{ color: info.allowedForCountry ? 'var(--ds-green-success-600)' : 'var(--ds-orange-800)' }}>
                  {info.allowedForCountry ? 'open' : 'CLOSED for a real buyer'}
                </span>
              </div>
            </div>
            {countryPreview && !info.allowedForCountry && (
              <div style={{ marginTop: 6, color: 'var(--ds-orange-800)' }}>
                You are seeing this because the preview bypasses the gate — a real buyer in {countryPreview} gets
                “not available in your region”.
              </div>
            )}
            {countryPreview && !info.pricedGeos.includes(countryPreview) && (
              <div style={{ marginTop: 6, color: 'var(--ds-orange-800)' }}>
                No currency is configured for {countryPreview}, so its plans have no price of their own —
                agent-payments substitutes the US ladder, and a buyer there is quoted USD. (Israel is the
                one geo excluded from that substitution: there the picker would be empty.)
              </div>
            )}
          </DebugSection>
        )}

        <DebugSection title="Reset (testing)">
          <div style={{ color: 'var(--ds-text-secondary)', marginBottom: 8 }}>
            Refunds your own purchases, cancels your subscription, and zeroes your credits — back to a
            fresh, never-purchased user so you can re-test the picker. Affects only your account.
          </div>
          {resetState === 'confirming' ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ color: 'var(--ds-fill-destructive-active)' }}>Reset your account?</span>
              <button
                type="button"
                onClick={runReset}
                style={{ border: 'none', borderRadius: 8, padding: '6px 12px', background: 'var(--ds-fill-destructive-active)', color: 'var(--ink-on-fill)', cursor: 'pointer', fontFamily: ARCHIVO, fontSize: 12 }}
              >
                Yes, reset
              </button>
              <button
                type="button"
                onClick={() => setResetState('idle')}
                style={{ border: '1px solid var(--ds-separator-default)', borderRadius: 8, padding: '6px 12px', background: 'var(--ds-surface-sunken)', cursor: 'pointer', fontFamily: ARCHIVO, fontSize: 12 }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={resetState === 'running'}
              onClick={() => setResetState('confirming')}
              style={{
                border: '1px solid var(--ds-fill-destructive-active)',
                borderRadius: 8,
                padding: '6px 12px',
                background: 'var(--ds-surface-sunken)',
                color: 'var(--ds-fill-destructive-active)',
                cursor: resetState === 'running' ? 'default' : 'pointer',
                opacity: resetState === 'running' ? 0.6 : 1,
                fontFamily: ARCHIVO,
                fontSize: 12,
              }}
            >
              {resetState === 'running' ? 'Resetting…' : 'Reset my account'}
            </button>
          )}
          {resetMsg && (
            <div style={{ marginTop: 8, color: resetState === 'error' ? 'var(--ds-fill-destructive-active)' : 'var(--ds-green-success-600)' }}>{resetMsg}</div>
          )}
        </DebugSection>

        <DebugSection title="Cohort (testing)">
          <div style={{ color: 'var(--ds-text-secondary)', marginBottom: 8 }}>
            Force your credits cohort, bypassing the created-date cutoff. Only takes effect for
            Wix staff, and changes only your account.
          </div>
          <div style={row}>
            <span>current</span>
            <span>{cohortMode ?? '—'}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {COHORTS.map((c) => (
              <button
                key={c.value}
                type="button"
                disabled={cohortBusy}
                onClick={() => applyCohort(c.value)}
                style={cohortBtn(cohortMode === c.value)}
              >
                {c.label}
              </button>
            ))}
            <button
              type="button"
              disabled={cohortBusy}
              onClick={() => applyCohort('CREDITS_MODE_UNKNOWN')}
              style={cohortBtn(false)}
            >
              Clear
            </button>
          </div>
          {cohortMsg && <div style={{ marginTop: 8, color: 'var(--ds-surface-sunken-dark)' }}>{cohortMsg}</div>}
        </DebugSection>

        {/* Read from the Stripe key's own prefix, never asserted. This section used to state "this is
            Stripe test mode, pay with 4242…" as a fact, which became a lie — and a costly one — the
            day the picker moved to live keys. */}
        {info && (
          <DebugSection title="Checkout mode">
            <div style={row}><span>billing lane</span><span>{info.billingLane}</span></div>
            <div style={row}><span>namespace</span><span>{info.billingNamespace}</span></div>
            <div style={row}>
              <span>expected Stripe account</span>
              <span>{info.expectedStripeAccountId || 'not configured'}</span>
            </div>
            <div style={{ ...row, marginBottom: 6 }}>
              <span>SCA configured</span>
              <span>{info.scaConfigured ? 'yes' : 'no'}</span>
            </div>
            {info.stripeMode === 'test' ? (
              <>
                <div style={{ color: 'var(--ds-text-secondary)', marginBottom: 6 }}>
                  Stripe <strong>test</strong> mode — no real money moves. At the payment step use:
                </div>
                <div style={{ padding: '2px 0' }}><strong>4242 4242 4242 4242</strong></div>
                <div style={{ color: 'var(--ds-text-secondary)' }}>
                  Any future expiry (e.g. 12/34) · any 3-digit CVC · any name &amp; postal code (ZIP 42424).
                </div>
                <div style={{ color: 'var(--ds-text-secondary)', marginTop: 6 }}>
                  Done testing? Hit “Reset my account” above and run through it again from scratch.
                </div>
              </>
            ) : info.stripeMode === 'live' ? (
              <div style={{ color: 'var(--ds-fill-destructive-active)' }}>
                Stripe <strong>LIVE</strong> mode — a checkout here charges a real card. Test cards are
                rejected. “Reset my account” issues a REAL refund.
              </div>
            ) : (
              <div style={{ color: 'var(--ds-orange-800)' }}>
                Stripe mode unknown — the secret key is missing or not a recognized key prefix, so
                assume a checkout is real.
              </div>
            )}
          </DebugSection>
        )}

        <DebugSection title="Credits">
          {premium ? (
            <>
              <div style={row}><span>balance</span><span>{premium.creditsBalance.toLocaleString()}</span></div>
              <div style={row}><span>subscription</span><span>{premium.subscriptionCredits.toLocaleString()}</span></div>
              <div style={row}><span>top-up</span><span>{premium.topupCredits.toLocaleString()}</span></div>
            </>
          ) : (
            <div style={{ color: 'var(--ds-fill-destructive-active)' }}>no premium data{premiumError ? ` — ${premiumError}` : ''}</div>
          )}
        </DebugSection>

        <DebugSection title={`Subscriptions (${premium?.subscriptions.length ?? 0})`}>
          {premium && premium.subscriptions.length > 0 ? (
            premium.subscriptions.map((s, i) => {
              // Resolve the raw plan_id against the loaded catalog so the panel names the plan and
              // its credits/price, not just a bare GUID. Null when plans haven't loaded or the
              // subscription's plan isn't in the current catalog (e.g. a legacy/retired plan).
              const resolved = findOption(plans, s.planId);
              const cad = cadenceOf(s);
              const priceAmount = amountFor(resolved?.option, cad);
              return (
                <div key={s.providerSubscriptionId || s.planId || i} style={{ padding: '6px 0', borderTop: i ? '1px solid var(--ds-surface-light-gray)' : 'none' }}>
                  {resolved ? (
                    <>
                      <div style={row}><span>plan</span><span style={{ fontWeight: 'var(--sym-weight-semibold)' }}>{t(resolved.plan.nameKey)}</span></div>
                      <div style={row}><span>credits</span><span>{resolved.option.credits.toLocaleString()}</span></div>
                      {/* formatMoney, not a hardcoded "$" — that quoted dollars at a GB/EUR subscriber. */}
                      {priceAmount != null && (
                        <div style={row}><span>price</span><span>{formatMoney(priceAmount, currency)} / {cad === 'yearly' ? 'year' : 'month'}</span></div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: 'var(--ds-orange-800)', paddingBottom: 4 }}>{plans ? 'plan not in catalog' : 'catalog not loaded'}</div>
                  )}
                  <div style={row}><span>plan_id</span><span>{s.planId}</span></div>
                  <div style={row}><span>status</span><span>{s.status ?? '—'}</span></div>
                  <div style={row}><span>period</span><span>{s.period ?? '—'}</span></div>
                  <div style={row}><span>stripe_sub</span><span>{s.providerSubscriptionId ?? '—'}</span></div>
                  {s.currentPeriodEnd && <div style={row}><span>period_end</span><span>{String(s.currentPeriodEnd)}</span></div>}
                </div>
              );
            })
          ) : (
            <div style={{ color: 'var(--ds-text-secondary)' }}>none</div>
          )}
        </DebugSection>

        <DebugSection title={`Active plan ids (${activePlanIds.size})`}>
          <div style={{ wordBreak: 'break-all', color: activePlanIds.size ? 'var(--ds-green-success-600)' : 'var(--ds-text-secondary)' }}>
            {activePlanIds.size ? [...activePlanIds].join(', ') : 'none'}
          </div>
        </DebugSection>

        <DebugSection title="Raw /api/credits/overview (BFF)" collapsible>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(overviewJson, null, 2)}
          </pre>
        </DebugSection>

        <DebugSection title="Raw /api/plans (query agent plans)" collapsible defaultOpen={false}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(plans, null, 2)}
          </pre>
        </DebugSection>
      </ScrollArea>
    </div>
  );
}

function DebugSection({
  title,
  children,
  collapsible,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  /** Render the header as a toggle. Non-collapsible sections always show their body. */
  collapsible?: boolean;
  /** Initial open state for a collapsible section. */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const showBody = !collapsible || open;
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        style={{
          fontFamily: ARCHIVO,
          fontSize: 11,
          fontWeight: 'var(--sym-weight-semibold)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--ds-control-fill-off-hover)',
          marginBottom: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        {collapsible && (
          <span aria-hidden style={{ fontSize: 8, transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
        )}
        {title}
      </div>
      {showBody && children}
    </div>
  );
}

// Top-of-picker banner for a pending deferred downgrade, with an Undo affordance.
function PendingChangeBanner({
  planName,
  date,
  onUndo,
  undoing,
}: {
  planName: string;
  date: string;
  onUndo: () => void;
  undoing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        margin: '20px auto 0',
        maxWidth: 560,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 15px',
        background: 'rgba(var(--ds-text-in-progress-rgb), 0.08)',
        borderRadius: 'var(--sym-radius-container)',
      }}
    >
      <span aria-hidden style={{ fontSize: 15, lineHeight: 1 }}>
        ⏳
      </span>
      <span style={{ flex: 1, fontFamily: MADEFOR_TEXT, fontSize: 13, lineHeight: 1.4, color: INK }}>
        {t('pricing.pendingBanner.prefix')} <strong>{planName}</strong>
        {date ? t('pricing.pendingBanner.suffixWithDate', { date }) : t('pricing.pendingBanner.suffix')}
      </span>
      <button
        type="button"
        onClick={onUndo}
        disabled={undoing}
        style={{
          appearance: 'none',
          border: 'none',
          background: 'transparent',
          color: BADGE_BLUE,
          fontFamily: ARCHIVO,
          fontWeight: 'var(--sym-weight-semibold)',
          fontSize: 13,
          textDecoration: 'underline',
          cursor: undoing ? 'default' : 'pointer',
          opacity: undoing ? 0.6 : 1,
          flexShrink: 0,
        }}
      >
        {undoing ? t('pricing.undoing') : t('pricing.undo')}
      </button>
    </div>
  );
}

// A switch charges the card on file with no Stripe checkout screen. A DOWNGRADE is a light
// confirm (deferred, no charge). An UPGRADE *is a purchase* — charged in full right now — so it
// gets a deliberate review-and-pay screen (order summary, amount due today, an explicit "Pay $X"
// action, and an in-dialog processing state) rather than a one-line "are you sure".
function SwitchConfirmModal({
  confirm,
  card,
  processing,
  onConfirm,
  onClose,
}: {
  confirm: SwitchConfirm;
  card: CardOnFile | null;
  processing: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isDowngrade = confirm.kind === 'downgrade';
  // priceLabel is "$200 / month" — split the amount out for the "due today" line + Pay button.
  const dueToday = (confirm.priceLabel || '').split(' / ')[0] || confirm.priceLabel;
  // "visa" → "Visa"; expiry → "04/28". Shown only when the card was resolved.
  const cardLabel =
    card?.hasCard && card.last4
      ? `${card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : t('pricing.card.genericBrand')} •••• ${card.last4}`
      : null;
  const cardExpiry =
    card?.hasCard && card.expMonth && card.expYear
      ? `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`
      : null;

    
  return (
    <Modal
      open
      onClose={processing ? () => undefined : onClose}
      labelledBy="switch-confirm-title"
      width={460}
      maxHeightPx={650}
      scrollBody
      cardClassName="sym-picker-modal"
      title={
        isDowngrade ? (
          <h2 style={{ margin: '0 0 10px', fontFamily: ARCHIVO, fontWeight: 700, fontSize: 22, letterSpacing: '-0.02em', color: INK }}>
            {t('pricing.confirm.downgradeTitle', { planName: confirm.targetPlanName })}
          </h2>
        ) : (
          <>
            <div
              style={{
                fontFamily: ARCHIVO,
                fontWeight: 'var(--sym-weight-semibold)',
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: BADGE_BLUE,
                marginBottom: 6,
              }}
            >
              {t('pricing.confirm.reviewPay')}
            </div>
            <h2 style={{ margin: '0 0 2px', fontFamily: ARCHIVO, fontWeight: 700, fontSize: 24, letterSpacing: '-0.02em', color: INK }}>
              {confirm.targetPlanName}
            </h2>
            <div style={{ fontFamily: MADEFOR_TEXT, fontSize: 13.5, color: 'rgba(var(--text-label-rgb), 0.6)', marginBottom: 18 }}>
              {confirm.cadence === 'yearly' ? t('pricing.cadencePlan.yearly') : t('pricing.cadencePlan.monthly')}
            </div>
          </>
        )
      }
      footer={
        isDowngrade ? (
          <>
            <Button variant="text" onClick={onClose} disabled={processing}>
              {t('common.cancel')}
            </Button>
            <Button onClick={onConfirm} disabled={processing} loading={processing}>
              {processing ? t('pricing.confirm.scheduling') : t('pricing.confirm.scheduleChange')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="text" onClick={onClose} disabled={processing}>
              {t('common.cancel')}
            </Button>
            <Button onClick={onConfirm} disabled={processing} loading={processing}>
              {processing ? t('pricing.confirm.processing') : t('pricing.confirm.pay', { amount: dueToday })}
            </Button>
          </>
        )
      }
    >
      {isDowngrade ? (
        // ---- Downgrade: deferred, no charge — a light confirm. ----
        <p style={{ margin: '0 0 20px', fontFamily: MADEFOR_TEXT, fontSize: 14, lineHeight: 1.5, color: BODY_INK }}>
          {t('pricing.confirm.downgradeBody', {
            currentPlan: confirm.currentPlanName,
            date: confirm.effectiveDate || t('pricing.confirm.endOfBillingPeriod'),
            targetPlan: confirm.targetPlanName,
            credits: confirm.targetCreditsLabel,
          })}
        </p>
      ) : (
        // ---- Upgrade: a real purchase — review & pay. ----
        <>
          {/* Order summary */}
          <div style={{ background: 'var(--sym-bg-neutral)', borderRadius: 'var(--sym-radius-container)', padding: '4px 14px', marginBottom: 16 }}>
            <SummaryRow label={t('pricing.summary.credits')} value={confirm.targetCreditsLabel} />
            <SummaryRow label={t('pricing.summary.billing')} value={confirm.priceLabel || '—'} />
            <div style={{ height: 1, background: 'rgba(var(--hairline-rgb), 0.1)', margin: '2px 0' }} />
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '12px 0' }}>
              <span style={{ fontFamily: ARCHIVO, fontWeight: 700, fontSize: 15, color: INK }}>{t('pricing.summary.dueToday')}</span>
              <span style={{ fontFamily: ARCHIVO, fontWeight: 700, fontSize: 22, letterSpacing: '-0.02em', color: INK }}>
                {dueToday}
              </span>
            </div>
          </div>

          <p style={{ margin: '0 0 14px', fontFamily: MADEFOR_TEXT, fontSize: 13, lineHeight: 1.5, color: 'rgba(var(--text-label-rgb), 0.7)' }}>
            {confirm.cadence === 'yearly'
              ? t('pricing.upgrade.chargeNote.yearly')
              : t('pricing.upgrade.chargeNote.monthly')}
          </p>

          {/* The card that will be charged — real Stripe data (brand •••• last4 · expiry) when
              resolved, generic "card on file" otherwise. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              padding: '11px 13px',
              background: 'var(--sym-bg-neutral)',
              borderRadius: 'var(--sym-radius-container)',
              marginBottom: 10,
            }}
          >
            <svg width="26" height="18" viewBox="0 0 26 18" aria-hidden style={{ flexShrink: 0 }}>
              <rect x="0.5" y="0.5" width="25" height="17" rx="3" fill="none" stroke="rgba(var(--text-label-rgb), 0.35)" />
              <rect x="0.5" y="4" width="25" height="3.4" fill="rgba(var(--text-label-rgb), 0.35)" />
            </svg>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: MADEFOR_TEXT, fontSize: 14, fontWeight: 'var(--sym-weight-medium)', color: INK }}>
                {cardLabel ?? t('pricing.card.onFile')}
              </div>
              {cardExpiry && (
                <div style={{ fontFamily: MADEFOR_TEXT, fontSize: 12, color: 'rgba(var(--text-label-rgb), 0.55)', marginTop: 1 }}>
                  {t('pricing.card.expires', { expiry: cardExpiry })}
                </div>
              )}
            </div>
          </div>

          {/* Secure-payment trust line. TODO: swap the "Powered by Stripe" text for Stripe's
              official wordmark SVG (vendor it under public/) — text used here to avoid reproducing
              the logo inaccurately. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, color: 'rgba(var(--text-label-rgb), 0.5)' }}>
            <svg width="11" height="13" viewBox="0 0 11 13" aria-hidden style={{ flexShrink: 0 }}>
              <rect x="0.5" y="5.5" width="10" height="7" rx="1.4" fill="none" stroke="currentColor" />
              <path d="M2.75 5.5V3.75a2.75 2.75 0 0 1 5.5 0V5.5" fill="none" stroke="currentColor" />
            </svg>
            <span style={{ fontFamily: MADEFOR_TEXT, fontSize: 12 }}>{t('pricing.securePayment')}</span>
          </div>
        </>
      )}
    </Modal>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '12px 0' }}>
      <span style={{ fontFamily: MADEFOR_TEXT, fontSize: 13.5, color: 'rgba(var(--text-label-rgb), 0.6)' }}>{label}</span>
      <span style={{ fontFamily: MADEFOR_TEXT, fontSize: 14, fontWeight: 'var(--sym-weight-medium)', color: INK, textAlign: 'end' }}>{value}</span>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'success' | 'error' | 'muted'; children: ReactNode }) {
  const palette = {
    success: { bg: 'rgba(var(--ds-green-success-500-rgb), 0.12)', border: 'rgba(var(--ds-green-success-500-rgb), 0.4)', ink: 'var(--ds-green-success-600)' },
    error: { bg: 'rgba(var(--ds-red-400-rgb), 0.12)', border: 'rgba(var(--ds-red-400-rgb), 0.4)', ink: 'var(--ds-fill-destructive-active)' },
    muted: { bg: 'rgba(var(--hairline-rgb), 0.05)', border: 'rgba(var(--hairline-rgb), 0.12)', ink: 'rgba(var(--text-label-rgb), 0.7)' },
  }[tone];
  return (
    <div
      style={{
        margin: '20px auto 0',
        maxWidth: 560,
        padding: '11px 15px',
        background: palette.bg,
        borderRadius: 'var(--sym-radius-container)',
        fontFamily: MADEFOR_TEXT,
        fontSize: 13,
        textAlign: 'center',
        color: palette.ink,
      }}
    >
      {children}
    </div>
  );
}
